/**
 * Shared helpers for Trend Mapping configuration and local analysis.
 * These are written to be page-agnostic so other dashboards can reuse them.
 */
import _ from "lodash";
import moment from "moment";
import { store } from "..";
import { HOSPITAL_CARDS_TYPE, HOSPITAL_CARDS_LABELS } from "../types/hospital.type";
import { CO_TRANSFER_CARDS_TYPE, COMMUNITY_CARD_LABELS, TRANSFER_TYPE } from "../types/community-transfer.type";
import { DECEASED_CARDS_TYPE, DECEASED_CARDS_LABELS } from "../types/deceased.type";
import { ADMISSION_CARDS_TYPE, ADMISSION_CARDS_LABELS } from "../types/admission.type";
import { OVERALL_CARDS_TYPE, OVERALL_CARDS_LABELS } from "../types/overall.type";
import { CHART_FILTER_DAY_OF, CUSTOM_TAB_TYPE } from "../types/common.type";
import { QUESTION_INPUT_TYPE } from "../types/question.type";
import { chartsData, chartsOverallData, chartGroupBy } from "./charts-common";
import { filterCustomPatientData } from "./common";
import { TYPES } from "../data/hospital.data";
import { ADMISSION_TYPES } from "../data/admission.data";
import { OVERALL_PAGE_SUB_TYPE } from "../types/pages.type";

/**
 * Matching mode constant for trend analysis.
 * Options: "trendOnly" | "valuesOnly" | "weighted" | "percentageBased"
 * 
 * CURRENT MODE: "weighted"
 * =========================
 * 
 * "weighted" mode combines both trend direction and visual shape:
 * - 40% weight for slope similarity (trend direction)
 * - 60% weight for point-by-point similarity (visual shape)
 * 
 * This mode is ideal for matching similar charts even when the actual values differ.
 * It considers both the overall trend direction and the visual pattern/shape.
 * 
 * Example:
 * --------
 * Base trend:     [5, 4, 3, 2, 1]  → Slope: -1.0
 * Candidate A:    [10, 8, 6, 4, 2] → Slope: -2.0, Similar shape
 * - Slope similarity: ~50% (different rates but same direction)
 * - Point similarity: ~80% (same relative pattern, 2x scale)
 * - Final match: (50% * 0.4) + (80% * 0.6) = 68% ✅
 * 
 * Candidate B:    [5, 20, 3, 2, 1] → Slope: -1.0, Different shape
 * - Slope similarity: 100% (same slope)
 * - Point similarity: ~40% (spike in week 2 breaks pattern)
 * - Final match: (100% * 0.4) + (40% * 0.6) = 64% ⚠️
 * 
 * This provides better visual matching while still considering trend direction.
 * 
 * "percentageBased" mode:
 * =======================
 * 
 * Compares trends based on percentage distribution rather than absolute values.
 * Each point is converted to a percentage of the total sum, then compared.
 * 
 * Example:
 * --------
 * Base trend:     [8, 12, 8, 12]  → Total: 40 → Percentages: [20%, 30%, 20%, 30%]
 * Candidate:      [2, 3, 2, 3]    → Total: 10 → Percentages: [20%, 30%, 20%, 30%]
 * - Perfect match: 100% ✅ (same percentage distribution)
 * 
 * This mode is ideal for matching trends with similar relative patterns regardless of scale.
 */
export const TREND_MATCHING_MODE = "percentageBased";

/**
 * Trend weight constant for weighted matching mode.
 * Represents the percentage weight for trend shape (0-100).
 * The remaining percentage (100 - TREND_WEIGHT) is used for point values.
 */
export const TREND_WEIGHT = 60;

/**
 * Build an initial Trend Mapping configuration from a chart dialog context.
 */
export const buildInitialTrendConfig = ({
	pageType,
	dialogFilters,
	queryFilters,
	selectedCardType,
	selectedItems,
}) => {
	const filter = dialogFilters?.filter || {};

	return {
		pageType,
		dateRange: {
			startDate: filter.startDate || queryFilters?.startDate || null,
			endDate: filter.endDate || queryFilters?.endDate || null,
		},
		filterMode: "keepAll", // Default: keep all filters
		selectedFilterCards: [], // For "keepSome" mode
		// Seed with the card that opened the dialog
		selectedCards: selectedCardType ? [selectedCardType] : [],
		// Seed item-level selection for that card
		selectedItemsByCard:
			selectedCardType && Array.isArray(selectedItems)
				? { [selectedCardType]: [...selectedItems] }
				: {},
		// Matching mode defaults
		similarityTolerance: 10, // Default: 10% tolerance
	};
};

/**
 * Build a backend payload for trend analysis from config + dialog context.
 */
export const buildTrendPayload = ({
	pageType,
	dialogFilters,
	queryFilters,
	config,
	baseCardType,
}) => {
	const filter = dialogFilters?.filter || {};	
	return {
		pageType,
		baseCardType,
		dateRange: {
			startDate: filter.startDate || queryFilters?.startDate || null,
			endDate: filter.endDate || queryFilters?.endDate || null,
		},
		applyExistingFilters: !!config.applyExistingFilters,
		dashboardFilters: config.applyExistingFilters ? dialogFilters?.cardFilter || {} : {},
		selectedCards: config.selectedCards || [],
		selectedItemsByCard: config.selectedItemsByCard || {},
	};
};

/**
 * INTERNAL: Apply cardFilter filters to patient data (similar to updateFilterListData logic).
 * This applies filters sequentially based on priorityData order.
 */
const applyCardFiltersToPatients = (patients, cardFilter, priorityData, excludeCardTypes = [], dynamicCards = [], ninetyDaysData = [], filterListData = {}) => {
	if (!Array.isArray(patients) || !cardFilter) return patients;

	let filteredPatients = [...patients];

	// Track which card types have been processed (from priorityData)
	const processedCardTypes = new Set();
	
	// Apply filters in priority order (if priorityData exists)
	// This matches the behavior in hospital-common.js where priorityData determines the order
	if (priorityData && Array.isArray(priorityData) && priorityData.length > 0) {
		for (const ele of priorityData) {
			const cardType = ele.type;

			// Skip if this card type is excluded
			if (excludeCardTypes.includes(cardType)) {
				processedCardTypes.add(cardType); // Mark as processed even if excluded
				continue;
			}

			const selectedItems = cardFilter[cardType];
			if (!selectedItems || !Array.isArray(selectedItems) || selectedItems.length === 0) {
				processedCardTypes.add(cardType); // Mark as processed even if no items
				continue;
			}
			
			// Mark this card type as processed
			processedCardTypes.add(cardType);

			// Check if this is a custom card
			if (ele?.question?.isCustom) {
				// Use filterCustomPatientData for custom cards (same as hospital-common.js)
				filteredPatients = filterCustomPatientData(filteredPatients, cardFilter, ele);
				continue;
			}
			
			// Apply filter based on card type
			switch (cardType) {
				case HOSPITAL_CARDS_TYPE.INSURANCE_DATA:
				case CO_TRANSFER_CARDS_TYPE.INSURANCE_DATA:
				case DECEASED_CARDS_TYPE.INSURANCE_DATA:
				case ADMISSION_CARDS_TYPE.INSURANCE_DATA:
				case OVERALL_CARDS_TYPE.INSURANCE_DATA:
				case OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.insuranceId)
					);
					break;
				case HOSPITAL_CARDS_TYPE.DOCTOR_DATA:
				case CO_TRANSFER_CARDS_TYPE.DOCTOR_DATA:
				case DECEASED_CARDS_TYPE.DOCTOR_DATA:
				case ADMISSION_CARDS_TYPE.DOCTOR_DATA:
				case OVERALL_CARDS_TYPE.DOCTOR_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.doctorId)
					);
					break;
				case HOSPITAL_CARDS_TYPE.NURSE_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.nurseId)
					);
					break;
				case HOSPITAL_CARDS_TYPE.FLOORS_DATA:
				case CO_TRANSFER_CARDS_TYPE.FLOORS_DATA:
				case DECEASED_CARDS_TYPE.FLOORS_DATA:
				case ADMISSION_CARDS_TYPE.FLOORS_DATA:
				case OVERALL_CARDS_TYPE.FLOORS_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.floorId)
					);
					break;
				case HOSPITAL_CARDS_TYPE.DAYS_DATA:
				case CO_TRANSFER_CARDS_TYPE.DAYS_DATA:
				case DECEASED_CARDS_TYPE.DAYS_DATA:
				case ADMISSION_CARDS_TYPE.DAYS_DATA:
				case OVERALL_CARDS_TYPE.DAYS_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.day)
					);
					break;
				case HOSPITAL_CARDS_TYPE.DX_DATA:
				case ADMISSION_CARDS_TYPE.DX_DATA:
					filteredPatients = filteredPatients.filter((p) => {
						const dxIds = Array.isArray(p.dxIds) ? p.dxIds : [];
						return _.intersection(selectedItems, dxIds).length > 0;
					});
					break;
				case HOSPITAL_CARDS_TYPE.SHIFT_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.shiftName)
					);
					break;
				case HOSPITAL_CARDS_TYPE.RETURNS_DATA:
				case CO_TRANSFER_CARDS_TYPE.RETURNS_DATA:
					if (selectedItems.length === 1) {
						if (selectedItems[0] === "Returned") {
							filteredPatients = filteredPatients.filter((p) => p.wasReturned === true);
						} else if (selectedItems[0] === "Didn't Return") {
							filteredPatients = filteredPatients.filter((p) => p.wasReturned === false);
						}
					}
					break;
				case HOSPITAL_CARDS_TYPE.DCER_DATA:
					if (selectedItems.length === 1) {
						if (selectedItems[0] === "DC") {
							filteredPatients = filteredPatients.filter((p) => p.wasAdmitted === true);
						} else if (selectedItems[0] === "ER") {
							filteredPatients = filteredPatients.filter((p) => p.wasAdmitted === false);
						}
					}
					break;
				case HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS:
					if (selectedItems.length === 1) {
						if (selectedItems[0] === "newHospitalizations") {
							filteredPatients = filteredPatients.filter((p) => p.reHospitalization === false);
						} else if (selectedItems[0] === "reHospitalizations") {
							filteredPatients = filteredPatients.filter((p) => p.reHospitalization === true);
						}
					}
					break;
				case HOSPITAL_CARDS_TYPE.HOSPITAL_DATA:
				case ADMISSION_CARDS_TYPE.HOSPITAL_DATA:
					filteredPatients = filteredPatients.filter((p) => {
						const hid = p.hospitalId || (p.hospital && (p.hospital._id || p.hospital.id)) || p._id;
						return selectedItems.some((id) => String(hid) === String(id));
					});
					break;
				case HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA:
				case CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA:
				case DECEASED_CARDS_TYPE.NINETY_DAYS_DATA:
				case OVERALL_CARDS_TYPE.NINETY_DAYS_DATA:
				case ADMISSION_CARDS_TYPE.SIXTY_DAYS_DATA:
					// 90/60 days card uses ninetyDaysData/sixtyDaysData structure with items that have an 'ids' array
					// CRITICAL FIX: selectedItems might be keys like 'range_0', 'range_1' instead of _id values
					// We need to convert them to _id values first
					
					// CRITICAL: If ninetyDaysData parameter is empty, try to use filterListData items as fallback
					// filterListData items have the structure: { _id, key, ids: [...], ... } which is what we need
					let actualNinetyDaysData = ninetyDaysData;
					if ((!Array.isArray(ninetyDaysData) || ninetyDaysData.length === 0) && filterListData[cardType]) {
						const cardItems = filterListData[cardType] || [];
						// Only use filterListData if items have the ids property (same structure as ninetyDaysData)
						if (cardItems.length > 0 && cardItems[0] && Array.isArray(cardItems[0].ids)) {
							actualNinetyDaysData = cardItems;
						}
					}
					
					if (!Array.isArray(actualNinetyDaysData) || actualNinetyDaysData.length === 0) {
						// No data available - this is a critical issue
						console.warn(`[applyCardFiltersToPatients] No days data (ninetyDaysData/sixtyDaysData) available for cardType: ${cardType}`);
						console.warn(`[applyCardFiltersToPatients] This will result in no patients being matched. Check if data is being passed correctly.`);
						// Don't filter out all patients - instead, skip this filter to prevent data loss
						// This allows other filters to still work
						break;
					}
					
					// Normalize selectedItems - handle both objects and primitive values
					let normalizedSelectedItems = selectedItems.map(item => {
						// If item is an object, extract _id or id
						if (item && typeof item === 'object') {
							return item._id || item.id || item.key || null;
						}
						// If item is already a primitive (string/number), use it as-is
						return item;
					}).filter(Boolean);
					
					// Convert selectedItems (which might be keys like 'range_0') to actual _id values
					let convertedSelectedItems = normalizedSelectedItems;
					if (Array.isArray(normalizedSelectedItems) && normalizedSelectedItems.length > 0) {
						// Check if selectedItems look like keys (e.g., 'range_0', 'range_1') rather than IDs
						const looksLikeKeys = normalizedSelectedItems.some(item => 
							typeof item === 'string' && (item.startsWith('range_') || item.match(/^\d+$/))
						);
						
						if (looksLikeKeys) {
							// First try to find in filterListData (preferred source - has the actual card items)
							const cardItems = filterListData[cardType] || [];
							
							convertedSelectedItems = normalizedSelectedItems.map(selectedItem => {
								// Try to find in filterListData first (most reliable)
								let found = cardItems.find(item => 
									String(item.key || '') === String(selectedItem) ||
									String(item._id || '') === String(selectedItem) ||
									String(item.id || '') === String(selectedItem)
								);
								
								// If not found in filterListData, try actualNinetyDaysData (which might be from filterListData fallback)
								if (!found && Array.isArray(actualNinetyDaysData) && actualNinetyDaysData.length > 0) {
									found = actualNinetyDaysData.find(item => 
										String(item.key || '') === String(selectedItem) ||
										String(item._id || '') === String(selectedItem) ||
										String(item.id || '') === String(selectedItem)
									);
									
									// If not found and it's a range_* key, try to find by index
									if (!found && typeof selectedItem === 'string' && selectedItem.startsWith('range_')) {
										const index = parseInt(selectedItem.replace('range_', ''));
										if (!isNaN(index) && actualNinetyDaysData[index]) {
											found = actualNinetyDaysData[index];
										}
									}
								}
								
								// Return the _id if found, otherwise return the original (might be a valid _id)
								return found ? (found._id || found.id || selectedItem) : selectedItem;
							}).filter(Boolean);
						}
					}
					
					// Filter actualNinetyDaysData items by converted selected item IDs
					// Try multiple matching strategies to handle different ID formats
					const ninetyDaysDataFiltered = actualNinetyDaysData.filter((item) => {
						const itemId = item._id || item.id;
						const itemKey = item.key;
						
						// Try exact match first
						const exactMatch = convertedSelectedItems.some((selectedId) => 
							String(itemId) === String(selectedId)
						);
						
						// Try key match (for range_* keys)
						const keyMatch = itemKey && convertedSelectedItems.some((selectedId) => 
							String(itemKey) === String(selectedId)
						);
						
						// Try case-insensitive string match
						const caseInsensitiveMatch = convertedSelectedItems.some((selectedId) => 
							String(itemId).toLowerCase() === String(selectedId).toLowerCase()
						);
						
						return exactMatch || keyMatch || caseInsensitiveMatch;
					});
					
					// Extract all patient IDs from filtered items
					let ninetyDaysDataIds = [];
					if (ninetyDaysDataFiltered.length > 0) {
						ninetyDaysDataFiltered.forEach((item) => {
							if (Array.isArray(item.ids) && item.ids.length > 0) {
								ninetyDaysDataIds = [...ninetyDaysDataIds, ...item.ids];
							}
						});
					}
					
					// Filter patients by those IDs
					if (ninetyDaysDataIds.length > 0) {
						filteredPatients = filteredPatients.filter((p) => {
							const patientId = String(p._id || p.id || "");
							return ninetyDaysDataIds.some((id) => String(id) === patientId);
						});

					} else {
						// No matching items found, filter out all patients
						console.warn(`[applyCardFiltersToPatients] ${cardType} - No matching ninetyDaysData items found, filtering out all patients`, {
							selectedItems: selectedItems?.slice(0, 3),
							actualNinetyDaysDataCount: actualNinetyDaysData?.length || 0,
							convertedSelectedItemsCount: convertedSelectedItems?.length || 0
						});
						filteredPatients = [];
					}
					break;
				case CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA:
					// Filter by assistantLivId (from safeDischargeAssLivData grouping)
					// Handle "all" special case
					if (selectedItems.includes("all")) {
						// "all" means no filter - keep all patients
						break;
					}
					filteredPatients = filteredPatients.filter((p) => {
						const assistantLivId = p.assistantLivId || 
							(p.transferToWhichAssistedLiving && (p.transferToWhichAssistedLiving._id || p.transferToWhichAssistedLiving.id));
						return assistantLivId && selectedItems.some((id) => String(assistantLivId) === String(id));
					});
					break;
				case CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA:
					// Filter by snfFacilityId (from snfFacilityData grouping)
					filteredPatients = filteredPatients.filter((p) => {
						const snfFacilityId = p.snfFacilityId || 
							(p.snf && (p.snf._id || p.snf.id));
						return snfFacilityId && selectedItems.some((id) => String(snfFacilityId) === String(id));
					});
					break;
				default:
					// For custom cards not in priorityData, find the card definition and use filterCustomPatientData
					const customCardDef = dynamicCards.find((c) => c.accessor === cardType);
					if (customCardDef && (customCardDef.isCustomTab || customCardDef.question?.isCustom || customCardDef.question?.customQuestionInputType)) {
						// Create a mock ele object for filterCustomPatientData (same structure as hospital-common.js)
						const mockEle = {
							type: cardType,
							question: {
								isCustom: true,
								customQuestionInputType: customCardDef.question?.customQuestionInputType,
							},
						};
						filteredPatients = filterCustomPatientData(filteredPatients, cardFilter, mockEle);
					} else {
						// Fallback: try filterId for unknown custom cards
						filteredPatients = filteredPatients.filter((p) =>
							p.filterId && selectedItems.includes(p.filterId)
						);
					}
					break;
			}
		}
	} else {
		// No priorityData, apply all filters directly in cardFilter order
		Object.keys(cardFilter).forEach((cardType) => {
			if (excludeCardTypes.includes(cardType)) {
				return;
			}

			const selectedItems = cardFilter[cardType];
			if (!selectedItems || !Array.isArray(selectedItems) || selectedItems.length === 0) {
				return;
			}

			// Check if this is a custom card
			const customCardDef = dynamicCards.find((c) => c.accessor === cardType);
			if (customCardDef && (customCardDef.isCustomTab || customCardDef.question?.isCustom || customCardDef.question?.customQuestionInputType)) {
				// Use filterCustomPatientData for custom cards (same as hospital-common.js)
				const mockEle = {
					type: cardType,
					question: {
						isCustom: true,
						customQuestionInputType: customCardDef.question?.customQuestionInputType,
					},
				};
				filteredPatients = filterCustomPatientData(filteredPatients, cardFilter, mockEle);
				return;
			}

			// Apply same filter logic as priorityData case
			switch (cardType) {
				case HOSPITAL_CARDS_TYPE.INSURANCE_DATA:
				case CO_TRANSFER_CARDS_TYPE.INSURANCE_DATA:
				case DECEASED_CARDS_TYPE.INSURANCE_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.insuranceId)
					);
					break;
				case HOSPITAL_CARDS_TYPE.DOCTOR_DATA:
				case CO_TRANSFER_CARDS_TYPE.DOCTOR_DATA:
				case DECEASED_CARDS_TYPE.DOCTOR_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.doctorId)
					);
					break;
				case HOSPITAL_CARDS_TYPE.NURSE_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.nurseId)
					);
					break;
				case HOSPITAL_CARDS_TYPE.FLOORS_DATA:
				case CO_TRANSFER_CARDS_TYPE.FLOORS_DATA:
				case DECEASED_CARDS_TYPE.FLOORS_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.floorId)
					);
					break;
				case HOSPITAL_CARDS_TYPE.DAYS_DATA:
				case CO_TRANSFER_CARDS_TYPE.DAYS_DATA:
				case DECEASED_CARDS_TYPE.DAYS_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.day)
					);
					break;
				case HOSPITAL_CARDS_TYPE.DX_DATA:
					filteredPatients = filteredPatients.filter((p) => {
						const dxIds = Array.isArray(p.dxIds) ? p.dxIds : [];
						return _.intersection(selectedItems, dxIds).length > 0;
					});
					break;
				case HOSPITAL_CARDS_TYPE.SHIFT_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.shiftName)
					);
					break;
				case HOSPITAL_CARDS_TYPE.RETURNS_DATA:
				case CO_TRANSFER_CARDS_TYPE.RETURNS_DATA:
					if (selectedItems.length === 1) {
						if (selectedItems[0] === "Returned") {
							filteredPatients = filteredPatients.filter((p) => p.wasReturned === true);
						} else if (selectedItems[0] === "Didn't Return") {
							filteredPatients = filteredPatients.filter((p) => p.wasReturned === false);
						}
					}
					break;
				case HOSPITAL_CARDS_TYPE.DCER_DATA:
					if (selectedItems.length === 1) {
						if (selectedItems[0] === "DC") {
							filteredPatients = filteredPatients.filter((p) => p.wasAdmitted === true);
						} else if (selectedItems[0] === "ER") {
							filteredPatients = filteredPatients.filter((p) => p.wasAdmitted === false);
						}
					}
					break;
				case HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS:
					if (selectedItems.length === 1) {
						if (selectedItems[0] === "newHospitalizations") {
							filteredPatients = filteredPatients.filter((p) => p.reHospitalization === false);
						} else if (selectedItems[0] === "reHospitalizations") {
							filteredPatients = filteredPatients.filter((p) => p.reHospitalization === true);
						}
					}
					break;
				case HOSPITAL_CARDS_TYPE.HOSPITAL_DATA:
				case ADMISSION_CARDS_TYPE.HOSPITAL_DATA:
					filteredPatients = filteredPatients.filter((p) => {
						const hid = p.hospitalId || (p.hospital && (p.hospital._id || p.hospital.id)) || p._id;
						return selectedItems.some((id) => String(hid) === String(id));
					});
					break;
				case HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA:
				case CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA:
				case DECEASED_CARDS_TYPE.NINETY_DAYS_DATA:
				case OVERALL_CARDS_TYPE.NINETY_DAYS_DATA:
					// 90 days card uses ninetyDaysData structure with items that have an 'ids' array
					// CRITICAL FIX: selectedItems might be keys like 'range_0', 'range_1' instead of _id values
					// We need to convert them to _id values first
					// CRITICAL: If ninetyDaysData parameter is empty, try to use filterListData items as fallback
					let actualNinetyDaysDataNoPriority = ninetyDaysData;
					if ((!Array.isArray(ninetyDaysData) || ninetyDaysData.length === 0) && filterListData[cardType]) {
						const cardItems = filterListData[cardType] || [];
						// Only use filterListData if items have the ids property (same structure as ninetyDaysData)
						if (cardItems.length > 0 && cardItems[0] && Array.isArray(cardItems[0].ids)) {
							actualNinetyDaysDataNoPriority = cardItems;
						}
					}
					
					if (!Array.isArray(actualNinetyDaysDataNoPriority) || actualNinetyDaysDataNoPriority.length === 0) {
						// No ninetyDaysData available, filter out all patients
						filteredPatients = [];
						break;
					}
					
					// Convert selectedItems (which might be keys like 'range_0') to actual _id values
					let convertedSelectedItemsNoPriority = selectedItems;
					if (Array.isArray(selectedItems) && selectedItems.length > 0) {
						// Check if selectedItems look like keys (e.g., 'range_0', 'range_1') rather than IDs
						const looksLikeKeysNoPriority = selectedItems.some(item => 
							typeof item === 'string' && (item.startsWith('range_') || item.match(/^\d+$/))
						);
						
						if (looksLikeKeysNoPriority) {
							// First try to find in filterListData (preferred source - has the actual card items)
							const cardItemsNoPriority = filterListData[cardType] || [];
							
							convertedSelectedItemsNoPriority = selectedItems.map(selectedItem => {
								// Try to find in filterListData first (most reliable)
								let found = cardItemsNoPriority.find(item => 
									String(item.key || '') === String(selectedItem) ||
									String(item._id || '') === String(selectedItem) ||
									String(item.id || '') === String(selectedItem)
								);
								
								// If not found in filterListData, try actualNinetyDaysDataNoPriority
								if (!found && Array.isArray(actualNinetyDaysDataNoPriority) && actualNinetyDaysDataNoPriority.length > 0) {
									found = actualNinetyDaysDataNoPriority.find(item => 
										String(item.key || '') === String(selectedItem) ||
										String(item._id || '') === String(selectedItem) ||
										String(item.id || '') === String(selectedItem)
									);
									
									// If not found and it's a range_* key, try to find by index
									if (!found && typeof selectedItem === 'string' && selectedItem.startsWith('range_')) {
										const index = parseInt(selectedItem.replace('range_', ''));
										if (!isNaN(index) && actualNinetyDaysDataNoPriority[index]) {
											found = actualNinetyDaysDataNoPriority[index];
										}
									}
								}
								
								// Return the _id if found, otherwise return the original (might be a valid _id)
								return found ? (found._id || found.id || selectedItem) : selectedItem;
							}).filter(Boolean);
						}
					}
					
					// Filter ninetyDaysData items by converted selected item IDs
					const ninetyDaysDataFilteredNoPriority = actualNinetyDaysDataNoPriority.filter((item) => {
						const itemId = item._id || item.id;
						return convertedSelectedItemsNoPriority.some((selectedId) => String(itemId) === String(selectedId));
					});
					
					// Extract all patient IDs from filtered items
					let ninetyDaysDataIdsNoPriority = [];
					if (ninetyDaysDataFilteredNoPriority.length > 0) {
						ninetyDaysDataFilteredNoPriority.forEach((item) => {
							if (Array.isArray(item.ids) && item.ids.length > 0) {
								ninetyDaysDataIdsNoPriority = [...ninetyDaysDataIdsNoPriority, ...item.ids];
							}
						});
					}
					
					// Filter patients by those IDs
					if (ninetyDaysDataIdsNoPriority.length > 0) {
						filteredPatients = filteredPatients.filter((p) => {
							const patientId = String(p._id || p.id || "");
							return ninetyDaysDataIdsNoPriority.some((id) => String(id) === patientId);
						});
					} else {
						// No matching items found, filter out all patients
						filteredPatients = [];
					}
					break;
				case CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA:
					// Filter by assistantLivId (from safeDischargeAssLivData grouping)
					// Handle "all" special case
					if (selectedItems.includes("all")) {
						// "all" means no filter - keep all patients
						break;
					}
					filteredPatients = filteredPatients.filter((p) => {
						const assistantLivId = p.assistantLivId || 
							(p.transferToWhichAssistedLiving && (p.transferToWhichAssistedLiving._id || p.transferToWhichAssistedLiving.id));
						return assistantLivId && selectedItems.some((id) => String(assistantLivId) === String(id));
					});
					break;
				case CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA:
					// Filter by snfFacilityId (from snfFacilityData grouping)
					filteredPatients = filteredPatients.filter((p) => {
						const snfFacilityId = p.snfFacilityId || 
							(p.snf && (p.snf._id || p.snf.id));
						return snfFacilityId && selectedItems.some((id) => String(snfFacilityId) === String(id));
					});
					break;
				default:
					// For custom cards not found, try filterId as fallback
					filteredPatients = filteredPatients.filter((p) =>
						p.filterId && selectedItems.includes(p.filterId)
					);
					break;
			}
		});
	}

	// CRITICAL FIX: Process cards that are in cardFilter but NOT in priorityData
	// IMPORTANT: In hospital-common.js, cards are only processed if they're in priorityData OR
	// if they're in the "remaining cards" section (lines 564-778). However, special filtering cards
	// like ninetyDaysData and sixtyDaysData are ONLY processed if they're in priorityData.
	// So we should NOT automatically process all cards in cardFilter - only process cards that
	// would be processed in hospital-common.js's "remaining cards" section.
	// 
	// Cards that should be processed here (if not in priorityData):
	// - hospitalizations, DCERData, insuranceData, returnsData, floorsData, doctorData, 
	//   daysData, dxData, shiftData, nurseData, hospitalData
	// Cards that should NOT be processed here (only if in priorityData):
	// - ninetyDaysData, sixtyDaysData (special filtering cards)
	if (priorityData && Array.isArray(priorityData) && priorityData.length > 0) {
		// Use the processedCardTypes Set that was already created and populated above
		// Process cards in cardFilter that weren't processed via priorityData
		// BUT exclude special filtering cards like ninetyDaysData and sixtyDaysData (they're only processed in priorityData)
		const specialFilteringCards = [
			HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA,
			CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA,
			DECEASED_CARDS_TYPE.NINETY_DAYS_DATA,
			OVERALL_CARDS_TYPE.NINETY_DAYS_DATA,
			ADMISSION_CARDS_TYPE.SIXTY_DAYS_DATA
		];
		
		for (const cardType of Object.keys(cardFilter)) {
			// Skip if already processed, excluded, or not a valid filter
			if (processedCardTypes.has(cardType) || 
				excludeCardTypes.includes(cardType) ||
				cardType === 'priorityData' ||
				cardType === 'mainPriorityData' ||
				specialFilteringCards.includes(cardType)) { // Skip special filtering cards
				continue;
			}

			const selectedItems = cardFilter[cardType];
			if (!selectedItems || !Array.isArray(selectedItems) || selectedItems.length === 0) {
				continue;
			}

			// Check if this is a custom card
			const customCardDef = dynamicCards.find((c) => c.accessor === cardType);
			if (customCardDef && (customCardDef.isCustomTab || customCardDef.question?.isCustom || customCardDef.question?.customQuestionInputType)) {
				// Use filterCustomPatientData for custom cards (same as hospital-common.js)
				const mockEle = {
					type: cardType,
					question: {
						isCustom: true,
						customQuestionInputType: customCardDef.question?.customQuestionInputType,
					},
				};
				filteredPatients = filterCustomPatientData(filteredPatients, cardFilter, mockEle);
				continue;
			}

			// Apply filter based on card type (same logic as priorityData loop)
			switch (cardType) {
				case HOSPITAL_CARDS_TYPE.INSURANCE_DATA:
				case CO_TRANSFER_CARDS_TYPE.INSURANCE_DATA:
				case DECEASED_CARDS_TYPE.INSURANCE_DATA:
				case ADMISSION_CARDS_TYPE.INSURANCE_DATA:
				case OVERALL_CARDS_TYPE.INSURANCE_DATA:
				case OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.insuranceId)
					);
					break;
				case HOSPITAL_CARDS_TYPE.DOCTOR_DATA:
				case CO_TRANSFER_CARDS_TYPE.DOCTOR_DATA:
				case DECEASED_CARDS_TYPE.DOCTOR_DATA:
				case ADMISSION_CARDS_TYPE.DOCTOR_DATA:
				case OVERALL_CARDS_TYPE.DOCTOR_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.doctorId)
					);
					break;
				case HOSPITAL_CARDS_TYPE.NURSE_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.nurseId)
					);
					break;
				case HOSPITAL_CARDS_TYPE.FLOORS_DATA:
				case CO_TRANSFER_CARDS_TYPE.FLOORS_DATA:
				case DECEASED_CARDS_TYPE.FLOORS_DATA:
				case ADMISSION_CARDS_TYPE.FLOORS_DATA:
				case OVERALL_CARDS_TYPE.FLOORS_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.floorId)
					);
					break;
				case HOSPITAL_CARDS_TYPE.DAYS_DATA:
				case CO_TRANSFER_CARDS_TYPE.DAYS_DATA:
				case DECEASED_CARDS_TYPE.DAYS_DATA:
				case ADMISSION_CARDS_TYPE.DAYS_DATA:
				case OVERALL_CARDS_TYPE.DAYS_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.day)
					);
					break;
				case HOSPITAL_CARDS_TYPE.DX_DATA:
				case ADMISSION_CARDS_TYPE.DX_DATA:
					filteredPatients = filteredPatients.filter((p) => {
						const dxIds = Array.isArray(p.dxIds) ? p.dxIds : [];
						return _.intersection(selectedItems, dxIds).length > 0;
					});
					break;
				case HOSPITAL_CARDS_TYPE.SHIFT_DATA:
					filteredPatients = filteredPatients.filter((p) =>
						selectedItems.includes(p.shiftName)
					);
					break;
				case HOSPITAL_CARDS_TYPE.RETURNS_DATA:
				case CO_TRANSFER_CARDS_TYPE.RETURNS_DATA:
					if (selectedItems.length === 1) {
						if (selectedItems[0] === "Returned") {
							filteredPatients = filteredPatients.filter((p) => p.wasReturned === true);
						} else if (selectedItems[0] === "Didn't Return") {
							filteredPatients = filteredPatients.filter((p) => p.wasReturned === false);
						}
					}
					break;
				case HOSPITAL_CARDS_TYPE.DCER_DATA:
					if (selectedItems.length === 1) {
						if (selectedItems[0] === "DC") {
							filteredPatients = filteredPatients.filter((p) => p.wasAdmitted === true);
						} else if (selectedItems[0] === "ER") {
							filteredPatients = filteredPatients.filter((p) => p.wasAdmitted === false);
						}
					}
					break;
				case HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS:
					if (selectedItems.length === 1) {
						if (selectedItems[0] === "newHospitalizations") {
							filteredPatients = filteredPatients.filter((p) => p.reHospitalization === false);
						} else if (selectedItems[0] === "reHospitalizations") {
							filteredPatients = filteredPatients.filter((p) => p.reHospitalization === true);
						}
					}
					break;
				case HOSPITAL_CARDS_TYPE.HOSPITAL_DATA:
				case ADMISSION_CARDS_TYPE.HOSPITAL_DATA:
					filteredPatients = filteredPatients.filter((p) => {
						const hid = p.hospitalId || (p.hospital && (p.hospital._id || p.hospital.id)) || p._id;
						return selectedItems.some((id) => String(hid) === String(id));
					});
					break;
				case HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA:
				case CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA:
				case DECEASED_CARDS_TYPE.NINETY_DAYS_DATA:
				case OVERALL_CARDS_TYPE.NINETY_DAYS_DATA:
				case ADMISSION_CARDS_TYPE.SIXTY_DAYS_DATA:
					// 90/60 days card uses ninetyDaysData/sixtyDaysData structure with items that have an 'ids' array
					// CRITICAL: If ninetyDaysData parameter is empty, try to use filterListData items as fallback
					// filterListData items have the structure: { _id, key, ids: [...], ... } which is what we need
					let actualNinetyDaysData = ninetyDaysData;
					if ((!Array.isArray(ninetyDaysData) || ninetyDaysData.length === 0) && filterListData[cardType]) {
						const cardItems = filterListData[cardType] || [];
						// Only use filterListData if items have the ids property (same structure as ninetyDaysData)
						if (cardItems.length > 0 && cardItems[0] && Array.isArray(cardItems[0].ids)) {
							actualNinetyDaysData = cardItems;
						}
					}
					
					if (!Array.isArray(actualNinetyDaysData) || actualNinetyDaysData.length === 0) {
						// No data available - skip filter to prevent data loss
						// This is safer than filtering out all patients
						break;
					}
					
					// CRITICAL FIX: Convert selectedItems (which might be keys like 'range_0', 'range_1') 
					// to actual _id values by looking them up in filterListData or ninetyDaysData
					let convertedSelectedItems2 = selectedItems;
					if (Array.isArray(selectedItems) && selectedItems.length > 0) {
						// Check if selectedItems look like keys (e.g., 'range_0', 'range_1') rather than IDs
						const looksLikeKeys2 = selectedItems.some(item => 
							typeof item === 'string' && (item.startsWith('range_') || item.match(/^\d+$/))
						);
						
						if (looksLikeKeys2) {
							// First try to find in filterListData (preferred source - has the actual card items)
							const cardItems2 = filterListData[cardType] || [];
							
							convertedSelectedItems2 = selectedItems.map(selectedItem => {
								// Try to find in filterListData first (most reliable)
								let found = cardItems2.find(item => 
									String(item.key || '') === String(selectedItem) ||
									String(item._id || '') === String(selectedItem) ||
									String(item.id || '') === String(selectedItem)
								);
								
								// If not found in filterListData, try actualNinetyDaysData
								if (!found && Array.isArray(actualNinetyDaysData) && actualNinetyDaysData.length > 0) {
									found = actualNinetyDaysData.find(item => 
										String(item.key || '') === String(selectedItem) ||
										String(item._id || '') === String(selectedItem) ||
										String(item.id || '') === String(selectedItem)
									);
									
									// If not found and it's a range_* key, try to find by index
									if (!found && typeof selectedItem === 'string' && selectedItem.startsWith('range_')) {
										const index = parseInt(selectedItem.replace('range_', ''));
										if (!isNaN(index) && actualNinetyDaysData[index]) {
											found = actualNinetyDaysData[index];
										}
									}
								}
								
								// Return the _id if found, otherwise return the original (might be a valid _id)
								return found ? (found._id || found.id || selectedItem) : selectedItem;
							}).filter(Boolean);
						}
					}
					
					// Filter ninetyDaysData items by converted selected item IDs
					const ninetyDaysDataFiltered2 = actualNinetyDaysData.filter((item) => {
						const itemId = item._id || item.id;
						return convertedSelectedItems2.some((selectedId) => String(itemId) === String(selectedId));
					});
					
					// Extract all patient IDs from filtered items
					let ninetyDaysDataIds2 = [];
					if (ninetyDaysDataFiltered2.length > 0) {
						ninetyDaysDataFiltered2.forEach((item) => {
							if (Array.isArray(item.ids) && item.ids.length > 0) {
								ninetyDaysDataIds2 = [...ninetyDaysDataIds2, ...item.ids];
							}
						});
					}
					
					// Filter patients by those IDs
					if (ninetyDaysDataIds2.length > 0) {
						filteredPatients = filteredPatients.filter((p) => {
							const patientId = String(p._id || p.id || "");
							return ninetyDaysDataIds2.some((id) => String(id) === patientId);
						});
					} else {
						// No matching items found, filter out all patients
						filteredPatients = [];
					}
					break;
				default:
					// For other card types, no special filtering needed in this context
					// They are handled by the main applyCardFiltersToPatients function
					break;
			}
		}
	}
	
	return filteredPatients;
};

/**
 * INTERNAL: filter global patient list by card type + item id (Hospital only).
 * This mirrors the field logic in updateFilterListData without re-dispatching.
 */
const filterPatientsByCardItemHospital = (patients, cardType, itemId, dynamicCards = [], filterListData = {}, ninetyDaysData = []) => {
			
	if (!Array.isArray(patients) || !cardType || itemId == null) {
		return [];
	}

	// Handle total/header cards (Total, Unplanned, Planned, AMA, SNF, Safe Discharge, Admission, Readmission)
	if (cardType === "total") {
		return patients;
	}
	if (cardType === "unplannedHospitalTransfer") {
		return patients.filter((p) => p.transferType === "unplannedHospitalTransfer");
	}
	if (cardType === "plannedHospitalTransfer") {
		return patients.filter((p) => p.transferType === "plannedHospitalTransfer");
	}
	if (cardType === "AMA") {
		return patients.filter((p) => p.transferType === "AMA");
	}
	if (cardType === "SNF") {
		return patients.filter((p) => p.transferType === "SNF");
	}
	if (cardType === "safeDischarge") {
		return patients.filter((p) => p.transferType === "safeDischarge");
	}
	if (cardType === "admission") {
		return patients.filter((p) => p.type === "admission");
	}
	if (cardType === "readmission") {
		return patients.filter((p) => p.type === "readmission");
	}

	// Check if this is a custom card - first check dynamicCards, then check if it's not a standard type
	const customCardDef = dynamicCards.find((c) => c.accessor === cardType);
	const isCustomFromDynamicCards = customCardDef && (customCardDef.isCustomTab || customCardDef.question?.isCustom || customCardDef.question?.customQuestionInputType);

	// Also check if it's a non-standard card type (custom cards added via fallback)
		const standardTypes = [
		// Hospital card types
		HOSPITAL_CARDS_TYPE.INSURANCE_DATA,
		HOSPITAL_CARDS_TYPE.DOCTOR_DATA,
		HOSPITAL_CARDS_TYPE.DX_DATA,
		HOSPITAL_CARDS_TYPE.DAYS_DATA,
		HOSPITAL_CARDS_TYPE.SHIFT_DATA,
		HOSPITAL_CARDS_TYPE.HOSPITAL_DATA,
		HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS,
		HOSPITAL_CARDS_TYPE.DCER_DATA,
		HOSPITAL_CARDS_TYPE.RETURNS_DATA,
		HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA,
		HOSPITAL_CARDS_TYPE.FLOORS_DATA,
		HOSPITAL_CARDS_TYPE.NURSE_DATA,
		// Community Transfer card types
		CO_TRANSFER_CARDS_TYPE.INSURANCE_DATA,
		CO_TRANSFER_CARDS_TYPE.DOCTOR_DATA,
		CO_TRANSFER_CARDS_TYPE.FLOORS_DATA,
		CO_TRANSFER_CARDS_TYPE.DAYS_DATA,
		CO_TRANSFER_CARDS_TYPE.RETURNS_DATA,
		CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA,
		CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA,
		CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA,
		// Deceased card types
		DECEASED_CARDS_TYPE.INSURANCE_DATA,
		DECEASED_CARDS_TYPE.DOCTOR_DATA,
		DECEASED_CARDS_TYPE.FLOORS_DATA,
		DECEASED_CARDS_TYPE.DAYS_DATA,
		DECEASED_CARDS_TYPE.NINETY_DAYS_DATA,
		// Admission card types
		ADMISSION_CARDS_TYPE.INSURANCE_DATA,
		ADMISSION_CARDS_TYPE.DOCTOR_DATA,
		ADMISSION_CARDS_TYPE.DX_DATA,
		ADMISSION_CARDS_TYPE.DAYS_DATA,
		ADMISSION_CARDS_TYPE.FLOORS_DATA,
		ADMISSION_CARDS_TYPE.HOSPITAL_DATA,
		ADMISSION_CARDS_TYPE.AVERAGE_STAY_DAYS_DATA,
		// Overall card types
		OVERALL_CARDS_TYPE.INSURANCE_DATA,
		OVERALL_CARDS_TYPE.DOCTOR_DATA,
		OVERALL_CARDS_TYPE.DAYS_DATA,
		OVERALL_CARDS_TYPE.FLOORS_DATA,
		OVERALL_CARDS_TYPE.NINETY_DAYS_DATA,
		OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA,
	];
	const isNonStandard = !standardTypes.includes(cardType);
	const hasFilterListData = filterListData[cardType] && Array.isArray(filterListData[cardType]) && filterListData[cardType].length > 0;

	if (isCustomFromDynamicCards || (isNonStandard && hasFilterListData)) {
		// For custom cards, filter using filterCustomPatientData (same as hospital-common.js)
		const questionType = customCardDef?.question?.customQuestionInputType;

		// Get the actual item from filterListData to extract the correct value
		const cardItems = filterListData[cardType] || [];
		const item = cardItems.find((i) => {
			const itemIdStr = String(itemId);
			return String(i._id || "") === itemIdStr ||
				String(i.id || "") === itemIdStr ||
				String(i.key || "") === itemIdStr ||
				String(i.name || "") === itemIdStr ||
				String(i.value || "") === itemIdStr;
		});


		// If we don't have questionType from dynamicCards, try to infer it from the item structure
		// If item has patientIds, it's likely a range type; otherwise it's likely an answer type
		const inferredQuestionType = questionType ||
			(item?.patientIds || item?.ids ? QUESTION_INPUT_TYPE.NUMBER_RANGE : QUESTION_INPUT_TYPE.LIMITED_ANSWERS);

		// For NUMBER_RANGE, TIME_TAB_RANGE, DATE_ANALYSIS, DATE: use patientIds from item
		if (inferredQuestionType === QUESTION_INPUT_TYPE.NUMBER_RANGE ||
			inferredQuestionType === QUESTION_INPUT_TYPE.NUMBER_RANGE_LIMITED_ANSWERS ||
			inferredQuestionType === QUESTION_INPUT_TYPE.TIME_TAB_RANGE ||
			inferredQuestionType === QUESTION_INPUT_TYPE.DATE_ANALYSIS ||
			inferredQuestionType === QUESTION_INPUT_TYPE.DATE) {
			// For range types, filter by patientIds using filterCustomPatientData
			const patientIds = item?.patientIds || item?.ids || [];
			if (Array.isArray(patientIds) && patientIds.length > 0) {
				// Create mockEle with patientIds for filterCustomPatientData
				const mockEle = {
					type: cardType,
					patientIds: patientIds,
					question: {
						isCustom: true,
						customQuestionInputType: inferredQuestionType,
					},
				};
				const cardFilter = { [cardType]: [] }; // Empty filter, patientIds will be used
				const filtered = filterCustomPatientData(patients, cardFilter, mockEle);
				return filtered;
			}
			return [];
		}

		// For UNLIMITED_ANSWERS and LIMITED_ANSWERS: use the actual value (name, label, value, or itemId itself)
		// The value should match what's stored in patient[accessor]
		// Try multiple possible values to match what might be in patient data
		const possibleValues = [
			item?.name,
			item?.label,
			item?.value,
			item?.key,
			itemId,
			// Also try string versions
			String(item?.name || ''),
			String(item?.label || ''),
			String(item?.value || ''),
			String(item?.key || ''),
			String(itemId || ''),
		].filter(Boolean);

		// Create a mock ele object and cardFilter with all possible values
		const mockEle = {
			type: cardType,
			question: {
				isCustom: true,
				customQuestionInputType: inferredQuestionType,
			},
		};
		const cardFilter = { [cardType]: possibleValues };
		const filtered = filterCustomPatientData(patients, cardFilter, mockEle);


		return filtered;
	}

	return patients.filter((p) => {
		switch (cardType) {
			case HOSPITAL_CARDS_TYPE.INSURANCE_DATA:
			case CO_TRANSFER_CARDS_TYPE.INSURANCE_DATA:
			case DECEASED_CARDS_TYPE.INSURANCE_DATA:
			case ADMISSION_CARDS_TYPE.INSURANCE_DATA:
			case OVERALL_CARDS_TYPE.INSURANCE_DATA:
				return p.insuranceId === itemId;
			case HOSPITAL_CARDS_TYPE.DOCTOR_DATA:
			case CO_TRANSFER_CARDS_TYPE.DOCTOR_DATA:
			case DECEASED_CARDS_TYPE.DOCTOR_DATA:
			case ADMISSION_CARDS_TYPE.DOCTOR_DATA:
			case OVERALL_CARDS_TYPE.DOCTOR_DATA:
				return p.doctorId === itemId;
			case HOSPITAL_CARDS_TYPE.NURSE_DATA:
				return p.nurseId === itemId;
			case HOSPITAL_CARDS_TYPE.FLOORS_DATA:
			case CO_TRANSFER_CARDS_TYPE.FLOORS_DATA:
			case DECEASED_CARDS_TYPE.FLOORS_DATA:
			case ADMISSION_CARDS_TYPE.FLOORS_DATA:
			case OVERALL_CARDS_TYPE.FLOORS_DATA:
				return p.floorId === itemId;
			case HOSPITAL_CARDS_TYPE.DAYS_DATA:
			case CO_TRANSFER_CARDS_TYPE.DAYS_DATA:
			case DECEASED_CARDS_TYPE.DAYS_DATA:
			case ADMISSION_CARDS_TYPE.DAYS_DATA:
			case OVERALL_CARDS_TYPE.DAYS_DATA:
				return p.day === itemId;
			case HOSPITAL_CARDS_TYPE.DX_DATA:
			case ADMISSION_CARDS_TYPE.DX_DATA: {
				const dxIds = Array.isArray(p.dxIds) ? p.dxIds : [];
				return _.includes(dxIds, itemId);
			}
			case HOSPITAL_CARDS_TYPE.SHIFT_DATA:
				return p.shiftName === itemId;
			case HOSPITAL_CARDS_TYPE.RETURNS_DATA:
			case CO_TRANSFER_CARDS_TYPE.RETURNS_DATA:
				// itemId is usually "Returned" / "Didn't Return" in UI;
				// here we just roughly map to boolean wasReturned for trend shape.
				if (itemId === "Returned") return p.wasReturned === true;
				if (itemId === "Didn't Return") return p.wasReturned === false;
				return false;
			case HOSPITAL_CARDS_TYPE.DCER_DATA:
				// itemId "DC"/"ER" mapped to wasAdmitted flag
				if (itemId === "DC") return p.wasAdmitted === true;
				if (itemId === "ER") return p.wasAdmitted === false;
				return false;
			case HOSPITAL_CARDS_TYPE.HOSPITALIZATIONS:
				// itemId "newHospitalizations"/"reHospitalizations" mapped to reHospitalization
				if (itemId === "newHospitalizations") return p.reHospitalization === false;
				if (itemId === "reHospitalizations") return p.reHospitalization === true;
				return false;
			case HOSPITAL_CARDS_TYPE.HOSPITAL_DATA:
			case ADMISSION_CARDS_TYPE.HOSPITAL_DATA: {
				const hid =
					p.hospitalId ||
					(p.hospital && (p.hospital._id || p.hospital.id)) ||
					p._id;
				return String(hid) === String(itemId);
			}
			case CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA:
			case CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA: {
				// These cards use filterId or relation-specific fields
				// Try filterId first, then try relation-specific matching
				if (p.filterId && String(p.filterId) === String(itemId)) {
					return true;
				}
				// For safeDischargeAssLivData, check transferToWhichAssistedLiving relation
				if (cardType === CO_TRANSFER_CARDS_TYPE.SAFE_DISCHARGE_ASS_LIV_DATA) {
					const relationId = p.transferToWhichAssistedLivingId || 
						(p.transferToWhichAssistedLiving && (p.transferToWhichAssistedLiving._id || p.transferToWhichAssistedLiving.id));
					if (relationId && String(relationId) === String(itemId)) {
						return true;
					}
				}
				// For snfFacilityData, check snf relation
				if (cardType === CO_TRANSFER_CARDS_TYPE.SNF_FACILITY_DATA) {
					const relationId = p.snfId || 
						(p.snf && (p.snf._id || p.snf.id));
					if (relationId && String(relationId) === String(itemId)) {
						return true;
					}
				}
				return false;
			}
			case OVERALL_CARDS_TYPE.EXHUAST_INSURANCE_DATA: {
				// Exhaust insurance card filters by insuranceId (similar to INSURANCE_DATA)
				// but only applies to exhausted Medicare patients
				return p.insuranceId === itemId;
			}
			case HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA:
			case CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA:
			case DECEASED_CARDS_TYPE.NINETY_DAYS_DATA:
			case OVERALL_CARDS_TYPE.NINETY_DAYS_DATA:
			case ADMISSION_CARDS_TYPE.SIXTY_DAYS_DATA: {
				// 90/60 days card uses ninetyDaysData/sixtyDaysData structure with items that have an 'ids' array
				// Find the item in filterListData or ninetyDaysData that matches itemId
				const itemIdStr = String(itemId);
				
				// Skip "average" item as it doesn't represent actual patient data
				if (itemIdStr === 'average' || itemIdStr.toLowerCase() === 'average') {
					return false;
				}
				
				// First try to find in filterListData (preferred source)
				const cardItems = filterListData[cardType] || [];
				let matchingItem = cardItems.find((i) => {
					return String(i._id || "") === itemIdStr ||
						String(i.id || "") === itemIdStr ||
						String(i.key || "") === itemIdStr ||
						String(i.name || "") === itemIdStr ||
						String(i.value || "") === itemIdStr;
				});
				
				// If found in filterListData but it doesn't have ids, try to find the corresponding item in ninetyDaysData by key
				if (matchingItem && (!matchingItem.ids || !Array.isArray(matchingItem.ids) || matchingItem.ids.length === 0)) {
					const itemKey = matchingItem.key || matchingItem._id || matchingItem.id;
					if (itemKey && Array.isArray(ninetyDaysData) && ninetyDaysData.length > 0) {
						const correspondingItem = ninetyDaysData.find((i) => {
							return String(i.key || "") === String(itemKey) ||
								String(i._id || "") === String(itemKey) ||
								String(i.id || "") === String(itemKey);
						});
						if (correspondingItem && Array.isArray(correspondingItem.ids) && correspondingItem.ids.length > 0) {
							matchingItem = correspondingItem;
						}
					}
				}
				
				// If not found in filterListData, try ninetyDaysData directly
				if (!matchingItem && Array.isArray(ninetyDaysData) && ninetyDaysData.length > 0) {
					matchingItem = ninetyDaysData.find((i) => {
						return String(i._id || "") === itemIdStr ||
							String(i.id || "") === itemIdStr ||
							String(i.key || "") === itemIdStr;
					});
				}
				
				if (!matchingItem) {	
					return false;
				}
				
				// Get patient IDs from the item's 'ids' array
				const patientIds = matchingItem.ids || matchingItem.patientIds || [];
				if (!Array.isArray(patientIds) || patientIds.length === 0) {
					return false;
				}
				
				// Check if patient's _id is in the patientIds array
				const patientId = String(p._id || p.id || "");
				const isIncluded = patientIds.some(id => String(id) === patientId);
				
				return isIncluded;
			}
			default:
				return false;
		}
	});
};

/**
 * INTERNAL: Filter series by isolated date ranges.
 * When isolated ranges are provided, only data points within those ranges are included.
 * Multiple ranges are handled as union (any point within any range is included).
 * This matches the logic in TrendMappingDialog.js filterSeriesByMultipleRanges.
 */
const filterSeriesByIsolatedRanges = (series, isolatedRanges, filterType = null) => {
	// Defensive checks: ensure inputs are valid
	if (!Array.isArray(series) || series.length === 0) return series;
	if (!Array.isArray(isolatedRanges) || isolatedRanges.length === 0) return series; // No filter applied

	return series.filter((point) => {
		// Defensive check: ensure point is an object
		if (!point || typeof point !== 'object') return true; // Keep invalid points

		// Prefer rawDate/x (canonical bucket date) when available
		const pointDate = point.rawDate || point.x || point.date || point.z;
		if (!pointDate) return true; // Keep points without dates

		// Try to parse the date - handle various formats and types
		let parsedDate = null;
		try {
			if (typeof pointDate === 'string') {
				// Try strict parsing first (handles MM-DD-YYYY, YYYY-MM-DD, etc.)
				parsedDate = moment(pointDate, ["MM-DD-YYYY", "YYYY-MM-DD", "MM/DD/YYYY", "YYYY/MM/DD", "MMM DD, YYYY"], true);
				if (!parsedDate.isValid()) {
					// Fallback to loose parsing
					parsedDate = moment(pointDate);
				}
			} else if (pointDate instanceof Date) {
				parsedDate = moment(pointDate);
			} else if (typeof pointDate === 'number') {
				// Handle timestamp
				parsedDate = moment(pointDate);
			}
		} catch (error) {
			// If parsing fails, keep the point (defensive)
			return true;
		}

		if (!parsedDate || !parsedDate.isValid()) {
			return true; // Keep points with unparseable dates
		}

		// Check if date is within any of the ranges (union - any range match includes the point)
		return isolatedRanges.some(range => {
			// Defensive check: ensure range is valid
			if (!range || typeof range !== 'object' || !range.start || !range.end) return false;
			
			try {
				const rangeStart = moment(range.start);
				const rangeEnd = moment(range.end);
				
				// Defensive check: ensure range dates are valid
				if (!rangeStart.isValid() || !rangeEnd.isValid()) return false;
				
				// Handle different filter types with appropriate comparison logic
				if (filterType === CHART_FILTER_DAY_OF.MONTH) {
					// For Monthly: rawDate is the start of the month
					// Compare by month/year - point's month should overlap with range's months
					const pointMonth = parsedDate.format('YYYY-MM');
					const rangeStartMonth = rangeStart.format('YYYY-MM');
					const rangeEndMonth = rangeEnd.format('YYYY-MM');
					return pointMonth >= rangeStartMonth && pointMonth <= rangeEndMonth;
				} else if (filterType === CHART_FILTER_DAY_OF.THIRTY) {
					// For 30 Days: rawDate is the end date of the 30-day bucket
					// The bucket spans from (rawDate - 29 days) to rawDate
					// Check if the bucket overlaps with the range
					const bucketStart = parsedDate.clone().subtract(29, 'days');
					const bucketEnd = parsedDate.clone();
					// Bucket overlaps if: bucketStart <= rangeEnd AND bucketEnd >= rangeStart
					return bucketStart.isSameOrBefore(rangeEnd, 'day') && bucketEnd.isSameOrAfter(rangeStart, 'day');
				} else if (filterType === CHART_FILTER_DAY_OF.SEVEN) {
					// For 7 Days: rawDate is the end date of the 7-day bucket
					// The bucket spans from (rawDate - 6 days) to rawDate
					// Check if the bucket overlaps with the range
					const bucketStart = parsedDate.clone().subtract(6, 'days');
					const bucketEnd = parsedDate.clone();
					// Bucket overlaps if: bucketStart <= rangeEnd AND bucketEnd >= rangeStart
					return bucketStart.isSameOrBefore(rangeEnd, 'day') && bucketEnd.isSameOrAfter(rangeStart, 'day');
				} else if (filterType === CHART_FILTER_DAY_OF.WEEK) {
					// For Weekly: rawDate is the start of the week
					// Compare by week boundaries
					const pointWeekStart = parsedDate.clone().startOf('week');
					const pointWeekEnd = parsedDate.clone().endOf('week');
					// Week overlaps if: pointWeekStart <= rangeEnd AND pointWeekEnd >= rangeStart
					return pointWeekStart.isSameOrBefore(rangeEnd, 'day') && pointWeekEnd.isSameOrAfter(rangeStart, 'day');
				} else {
					// For Daily and others: use day-level comparison
					return parsedDate.isSameOrAfter(rangeStart, 'day') &&
						   parsedDate.isSameOrBefore(rangeEnd, 'day');
				}
			} catch (error) {
				// If comparison fails, exclude the point (defensive)
				return false;
			}
		});
	});
};

/**
 * INTERNAL: build a time series for a set of patients using the same grouping logic as the main chart.
 * Uses chartGroupBy from charts-common.js to ensure consistency with the main chart display.
 * This ensures analysis results match the preview and main chart exactly.
 */
const buildSeriesForGroupType = async (patients, filter, groupBy, pageType = null, transferType = null) => {
	if (!Array.isArray(patients) || patients.length === 0 || !filter) return [];

	// For Overall page with TOTAL transfer type, use chartsOverallData to get +/- values
	// This matches how the main chart displays data (totalIN - totalOut)
	let raw;
	if (pageType === "overall" && transferType === OVERALL_PAGE_SUB_TYPE.TOTAL) {
		raw = await chartsOverallData(patients, filter);
	} else {
		raw = await chartsData(patients, filter);
	}
	if (!Array.isArray(raw) || raw.length === 0) return [];

	// If daily view, just return per-day points (maintains backward compatibility)
	if (!groupBy || groupBy === CHART_FILTER_DAY_OF.DAY) {
		return raw.map((pt) => ({
			date: pt.x, // Date string for display
			x: pt.x,    // Same date string for matching
			total: pt.total || 0,
		}));
	}

	// Use chartGroupBy to get the same grouping logic as the main chart
	// This ensures analysis results match the preview and main chart exactly
	// Extract census data from filter if available (for accurate monthly calculations)
	// The filter object may contain censusAverage and censusByPeriod from dialogFilters
	const censusAvg = filter?.censusAverage ? parseFloat(filter.censusAverage) || 0 : 0;
	const censusPeriod = filter?.censusByPeriod || [];
	const grouped = await chartGroupBy(raw, groupBy || CHART_FILTER_DAY_OF.DAY, censusAvg, censusPeriod, filter);
	
	if (!Array.isArray(grouped) || grouped.length === 0) return [];

	// Convert chartGroupBy format to our series format
	// chartGroupBy returns: { z: label, x: date (MM-DD-YYYY), y: binaryString, facilityWiseData: {...}, ... }
	// The y field is a binary string representation, not the actual count
	// We need to count patients from facilityWiseData (same logic as preview chart)
	// We need: { date: label (z), x: label (for period matching), total: count }
	// Use label (z) for matching because:
	// 1. Both base and candidate series use the same grouping logic, so they'll have matching labels
	// 2. Labels uniquely identify periods across all period types (day, week, month, 7-day, 30-day)
	// 3. This ensures consistent matching regardless of period type
	
		return grouped.map((item) => {
		let totalValue = 0;
		
		// If this is a projected data point, use the pre-calculated projected y value directly
		// The projection was already calculated in chartGroupBy, so we should use it as-is
		// This matches the logic in LineChartDialog.js
		if (item?.isProjected && item?.y != null && typeof item.y === 'number') {
			// Use the projected y value directly from chartGroupBy
			totalValue = item.y;
		} else {
			// For Overall page with TOTAL transfer type, use total directly (can be negative: totalIN - totalOut)
			// For other cases, use the same logic as before
			const isOverallTotal = pageType === "overall" && transferType === OVERALL_PAGE_SUB_TYPE.TOTAL;
			
			if (isOverallTotal) {
				// For TOTAL transfer type, total is totalIN - totalOut (can be negative)
				// Use item.total or item.y directly, both represent the net value
				if (typeof item.total === 'number') {
					totalValue = item.total;
				} else if (typeof item.total === 'string') {
					const parsed = parseFloat(item.total);
					if (!isNaN(parsed)) {
						totalValue = parsed;
					}
				} else if (typeof item.y === 'number') {
					// For chartsOverallData, y is also totalIN - totalOut
					totalValue = item.y;
				}
			} else {
				// For non-TOTAL transfer types, use the original logic
				// If total exists and is a number, use it
				if (typeof item.total === 'number') {
					totalValue = item.total;
				} else if (typeof item.total === 'string') {
					const parsed = parseFloat(item.total);
					if (!isNaN(parsed)) {
						totalValue = parsed;
					}
				}
				
				// If total is not available or is 0, count patients from facilityWiseData
				// This matches how the parent chart and preview chart calculate the count
				if (totalValue === 0 && item.facilityWiseData && typeof item.facilityWiseData === 'object') {
					// Count all patients across all facilities
					const facilityWiseData = item.facilityWiseData;
					totalValue = Object.values(facilityWiseData).reduce((sum, patients) => {
						return sum + (Array.isArray(patients) ? patients.length : 0);
					}, 0);
				}
				
				// Fallback: use facilityIds length if facilityWiseData not available
				if (totalValue === 0 && Array.isArray(item.facilityIds)) {
					totalValue = item.facilityIds.length;
				}
				
				// Last resort: try to parse y as number (but y is usually a binary string)
				if (totalValue === 0 && typeof item.y === 'number') {
					totalValue = item.y;
				}
			}
		}
		
		// IMPORTANT for isolation / bucket-based operations:
		// - date/x: human-readable label (e.g. "Nov", "(11-01)-(11-07)")
		// - rawDate: canonical bucket date from chartGroupBy (item.x, e.g. "MM-DD-YYYY")
		//   This is what we use when we need a real date for comparisons (day/week/month/7/30).
		return {
			date: item.z || item.x,
			x: item.z || item.x,
			rawDate: item.x,
			total: totalValue,
			isProjected: item?.isProjected || false,
			originalY: item?.originalY,
		};
	});
};

// NOTE: computeSeriesSimilarity function removed - currently using slope-based similarity instead
// If Pearson correlation-based similarity is needed in the future, this function can be restored

/**
 * INTERNAL: compute linear regression slope for a time series.
 * Uses x = index (0..n-1), y = total.
 */
const computeSlope = (series) => {
	if (!Array.isArray(series) || series.length < 2) return 0;
	const n = series.length;
	const xs = [];
	const ys = [];
	for (let i = 0; i < n; i++) {
		xs.push(i);
		ys.push(series[i].total || 0);
	}
	const sumX = _.sum(xs);
	const sumY = _.sum(ys);
	const sumXY = _.sum(xs.map((x, i) => x * ys[i]));
	const sumX2 = _.sum(xs.map((x) => x * x));
	const denom = n * sumX2 - sumX * sumX;
	if (!denom) return 0;
	const num = n * sumXY - sumX * sumY;
	return num / denom;
};

/**
 * INTERNAL: Convert series points to percentages based on total sum.
 * Returns a new series with percentage values instead of absolute values.
 */
const convertSeriesToPercentages = (series) => {
	if (!Array.isArray(series) || series.length === 0) {
		return [];
	}

	// Calculate total sum of all points
	const total = series.reduce((sum, point) => {
		return sum + Math.abs(point.total || 0);
	}, 0);

	// If total is zero, return series with all zeros (can't calculate percentages)
	if (total === 0) {
		return series.map((point) => ({
			...point,
			percentage: 0,
		}));
	}

	// Convert each point to percentage
	// Use Math.abs() to handle negative values (e.g., Overall page TOTAL transfer type)
	// This ensures percentage-based matching works correctly for all pages and card types
	return series.map((point) => {
		const value = Math.abs(point.total || 0);
		const percentage = (value / total) * 100;
		return {
			...point,
			percentage: percentage,
		};
	});
};

/**
 * INTERNAL: compute point-by-point similarity between two time series based on percentages.
 * Returns a similarity score from 0-100, where 100 is identical.
 * Handles series of different lengths by aligning by time period and padding missing periods with zeros.
 * Works correctly for all card types, pages, and filter types (daily, weekly, monthly, 7 days, 30 days).
 * 
 * CRITICAL: This function is ONLY called when matchingMode === "percentageBased".
 * Other modes (trendOnly, valuesOnly, weighted) use different similarity functions and are NOT affected.
 * 
 * ALIGNMENT LOGIC:
 * ================
 * The function aligns candidate series to base series by padding missing periods with zeros.
 * This ensures accurate percentage comparison when candidate has fewer data points.
 * 
 * Example (Monthly view):
 * - Base chart: January=4, February=2, March=6 (total=12, percentages: 33.3%, 16.7%, 50%)
 * - Candidate item: February=2 (only has February data)
 * - Aligned candidate: January=0, February=2, March=0 (total=2, percentages: 0%, 100%, 0%)
 * - Comparison: Calculate difference in percentages for each period
 * 
 * This same logic works for ALL filter types:
 * - Daily: Align by day (rawDate in "MM-DD-YYYY" format)
 * - Weekly: Align by week (rawDate is start of week)
 * - Monthly: Align by month (rawDate is start of month)
 * - 7 Days: Align by 7-day period (rawDate is end date of period)
 * - 30 Days: Align by 30-day period (rawDate is end date of period)
 * 
 * The function also applies a sparsity penalty for candidates with significantly fewer data points.
 */
const computePercentageBasedSimilarity = (baseSeries, candidateSeries) => {
	if (!Array.isArray(baseSeries) || !Array.isArray(candidateSeries) || baseSeries.length === 0 || candidateSeries.length === 0) {
		return 0;
	}

	// Check if both series have zero totals (all values are zero)
	const baseTotal = baseSeries.reduce((sum, point) => sum + Math.abs(point.total || 0), 0);
	const candidateTotal = candidateSeries.reduce((sum, point) => sum + Math.abs(point.total || 0), 0);
	
	// If both series have zero totals, they match perfectly (both represent empty/no data)
	if (baseTotal === 0 && candidateTotal === 0) {
		return 100;
	}
	
	// If one has zero total and the other doesn't, they don't match
	if (baseTotal === 0 || candidateTotal === 0) {
		return 0;
	}

	// Create a map of candidate points for alignment
	const candidatePointMap = new Map();
	candidateSeries.forEach((point) => {
		const pointData = {
			total: point.total || 0,
			x: point.x,
			rawDate: point.rawDate,
			date: point.date,
		};
		
		const rawDateKey = point.rawDate ? String(point.rawDate) : null;
		if (rawDateKey) {
			candidatePointMap.set(rawDateKey, pointData);
		}
		
		const xKey = point.x ? String(point.x) : null;
		if (xKey && xKey !== rawDateKey) {
			candidatePointMap.set(xKey, pointData);
		}
		
		const dateKey = point.date ? String(point.date) : null;
		if (dateKey && dateKey !== rawDateKey && dateKey !== xKey) {
			candidatePointMap.set(dateKey, pointData);
		}
	});
	
	// Align candidate series to base series: pad missing periods with zeros
	const alignedCandidateSeries = baseSeries.map((basePoint) => {
		let candidatePoint = null;
		
		const baseRawDate = basePoint.rawDate ? String(basePoint.rawDate) : null;
		if (baseRawDate && candidatePointMap.has(baseRawDate)) {
			candidatePoint = candidatePointMap.get(baseRawDate);
		}
		
		if (!candidatePoint) {
			const baseX = basePoint.x ? String(basePoint.x) : null;
			if (baseX && candidatePointMap.has(baseX)) {
				candidatePoint = candidatePointMap.get(baseX);
			}
		}
		
		if (!candidatePoint) {
			const baseDate = basePoint.date ? String(basePoint.date) : null;
			if (baseDate && candidatePointMap.has(baseDate)) {
				candidatePoint = candidatePointMap.get(baseDate);
			}
		}
		
		return {
			x: basePoint.x,
			rawDate: basePoint.rawDate,
			date: basePoint.date,
			total: candidatePoint ? candidatePoint.total : 0,
		};
	});

	// ==========================================
	// EXACT MATCH CHECK (Bypass percentage logic)
	// ==========================================
	let isExactMatch = true;
	for (let i = 0; i < baseSeries.length; i++) {
		const baseValue = Math.abs(baseSeries[i].total || 0);
		const candidateValue = Math.abs(alignedCandidateSeries[i].total || 0);
		if (baseValue !== candidateValue) {
			isExactMatch = false;
			break;
		}
	}

	// Verify the candidate didn't have extra non-zero data points outside the base's date range
	const candidateNonZeroCount = candidateSeries.filter(p => Math.abs(p.total || 0) > 0).length;
	const alignedNonZeroCount = alignedCandidateSeries.filter(p => Math.abs(p.total || 0) > 0).length;

	if (isExactMatch && candidateNonZeroCount === alignedNonZeroCount) {
		return 100; // Perfect exact match of raw numbers, bypass all percentage calculations!
	}
	// ==========================================
	
	// If not an exact match, proceed with percentage conversion
	const basePercentages = convertSeriesToPercentages(baseSeries);
	const candidatePercentages = convertSeriesToPercentages(alignedCandidateSeries);

	let totalDiff = 0;
	let totalPoints = 0;

	basePercentages.forEach((basePoint, index) => {
		const candidatePoint = candidatePercentages[index];
		
		const basePercentage = basePoint.percentage || 0;
		const candidatePercentage = candidatePoint ? (candidatePoint.percentage || 0) : 0;
		
		const diff = Math.abs(basePercentage - candidatePercentage);
		totalDiff += diff;
		totalPoints++;
	});

	if (totalPoints === 0) {
		return 0;
	}

	const avgDiff = totalDiff / totalPoints;
	let similarity = Math.max(0, Math.min(100, 100 - avgDiff));
	
	// Apply penalty for sparse data
	const baseNonZeroPoints = baseSeries.filter(p => Math.abs(p.total || 0) > 0).length;
	const candidateNonZeroPoints = candidateSeries.filter(p => Math.abs(p.total || 0) > 0).length;
	
	if (baseNonZeroPoints > 0 && candidateNonZeroPoints > 0) {
		const sparsityRatio = candidateNonZeroPoints / baseNonZeroPoints;
		
		// Only apply penalty if it's not a near-perfect percentage match
		if (sparsityRatio < 0.5 && similarity < 99.9) {
			const penalty = (1 - sparsityRatio) * 0.7;
			similarity = similarity * (1 - penalty);
		}
	}
	
	similarity = Math.max(0, Math.min(100, similarity));
	return parseFloat(similarity.toFixed(2));
};

// const computePercentageBasedSimilarityOld = (baseSeries, candidateSeries) => {
// 	if (!Array.isArray(baseSeries) || !Array.isArray(candidateSeries) || baseSeries.length === 0 || candidateSeries.length === 0) {
// 		return 0;
// 	}

// 	// Check if both series have zero totals (all values are zero)
// 	const baseTotal = baseSeries.reduce((sum, point) => sum + Math.abs(point.total || 0), 0);
// 	const candidateTotal = candidateSeries.reduce((sum, point) => sum + Math.abs(point.total || 0), 0);
	
// 	// If both series have zero totals, they match perfectly (both represent empty/no data)
// 	if (baseTotal === 0 && candidateTotal === 0) {
// 		return 100;
// 	}
	
// 	// If one has zero total and the other doesn't, they don't match
// 	if (baseTotal === 0 || candidateTotal === 0) {
// 		return 0;
// 	}

// 	// CRITICAL: For percentage-based matching, we need to align both series by time periods
// 	// and pad missing periods with zeros BEFORE calculating percentages
// 	// This ensures accurate comparison when candidate has fewer data points than base
// 	// 
// 	// Example: Base has [Jan=4, Feb=2, Mar=6], Candidate has [Feb=2]
// 	// Aligned candidate: [Jan=0, Feb=2, Mar=0]
// 	// Then calculate percentages: Base [33.3%, 16.7%, 50%], Candidate [0%, 100%, 0%]
// 	// This works for ALL filter types: daily, weekly, monthly, 7 days, 30 days
	
// 	// Create a map of candidate points for alignment
// 	// Priority: rawDate (canonical "MM-DD-YYYY") > x (human-readable label) > date
// 	// rawDate is most reliable because it's consistent across all filter types
// 	const candidatePointMap = new Map();
// 	candidateSeries.forEach((point) => {
// 		const pointData = {
// 			total: point.total || 0,
// 			x: point.x,
// 			rawDate: point.rawDate,
// 			date: point.date,
// 		};
		
// 		// Use rawDate as primary key (most reliable - consistent format "MM-DD-YYYY")
// 		const rawDateKey = point.rawDate ? String(point.rawDate) : null;
// 		if (rawDateKey) {
// 			candidatePointMap.set(rawDateKey, pointData);
// 		}
		
// 		// Also index by x (human-readable label like "January", "(01-01)-(01-07)")
// 		const xKey = point.x ? String(point.x) : null;
// 		if (xKey && xKey !== rawDateKey) {
// 			candidatePointMap.set(xKey, pointData);
// 		}
		
// 		// Also index by date (same as x, but might differ in some cases)
// 		const dateKey = point.date ? String(point.date) : null;
// 		if (dateKey && dateKey !== rawDateKey && dateKey !== xKey) {
// 			candidatePointMap.set(dateKey, pointData);
// 		}
// 	});
	
// 	// Align candidate series to base series: pad missing periods with zeros
// 	// For each period in base series, find matching candidate period or use 0
// 	const alignedCandidateSeries = baseSeries.map((basePoint) => {
// 		// Try to find matching candidate point using multiple keys
// 		// Priority: rawDate (most reliable) > x (human-readable) > date
// 		let candidatePoint = null;
		
// 		// 1. Try rawDate first (canonical "MM-DD-YYYY" format - works for all filter types)
// 		const baseRawDate = basePoint.rawDate ? String(basePoint.rawDate) : null;
// 		if (baseRawDate && candidatePointMap.has(baseRawDate)) {
// 			candidatePoint = candidatePointMap.get(baseRawDate);
// 		}
		
// 		// 2. Fallback to x (human-readable label like "January", "(01-01)-(01-07)")
// 		if (!candidatePoint) {
// 			const baseX = basePoint.x ? String(basePoint.x) : null;
// 			if (baseX && candidatePointMap.has(baseX)) {
// 				candidatePoint = candidatePointMap.get(baseX);
// 			}
// 		}
		
// 		// 3. Fallback to date (same as x in most cases)
// 		if (!candidatePoint) {
// 			const baseDate = basePoint.date ? String(basePoint.date) : null;
// 			if (baseDate && candidatePointMap.has(baseDate)) {
// 				candidatePoint = candidatePointMap.get(baseDate);
// 			}
// 		}
		
// 		// If no match found, use zero (missing data in that period)
// 		// This ensures candidate is aligned to base series structure
// 		return {
// 			x: basePoint.x,
// 			rawDate: basePoint.rawDate,
// 			date: basePoint.date,
// 			total: candidatePoint ? candidatePoint.total : 0,
// 		};
// 	});
	
// 	// Now convert both aligned series to percentages
// 	const basePercentages = convertSeriesToPercentages(baseSeries);
// 	const candidatePercentages = convertSeriesToPercentages(alignedCandidateSeries);

// 	// Compare each point in base series with corresponding point in aligned candidate series
// 	// Since both series are now aligned (same length, same x values), we can compare directly
// 	let totalDiff = 0;
// 	let totalPoints = 0;

// 	basePercentages.forEach((basePoint, index) => {
// 		const candidatePoint = candidatePercentages[index];
		
// 		// Both series are aligned, so we can compare directly
// 		const basePercentage = basePoint.percentage || 0;
// 		const candidatePercentage = candidatePoint ? (candidatePoint.percentage || 0) : 0;
		
// 		// Calculate absolute difference in percentages
// 		const diff = Math.abs(basePercentage - candidatePercentage);
// 		totalDiff += diff;
// 		totalPoints++;
// 	});

// 	// Calculate similarity percentage
// 	// We compare ALL base points (missing candidate points are treated as 0%)
// 	if (totalPoints === 0) {
// 		return 0;
// 	}

// 	const avgDiff = totalDiff / totalPoints;


// 	// Convert average difference to similarity: 0% diff = 100% similarity, 100% diff = 0% similarity
// 	let similarity = 0;
//     if (avgDiff < 0.000001) {
//         similarity = 100;
//     } else {
//         similarity = Math.max(0, Math.min(100, 100 - avgDiff));
//     }
	
// 	// CRITICAL: Apply penalty for sparse data
// 	// NOTE: This penalty is ONLY applied in percentageBased mode (this function is only called for percentageBased)
// 	// Other modes (trendOnly, valuesOnly, weighted) are NOT affected by this penalty
// 	// If candidate has significantly fewer data points than base, reduce similarity
// 	// This prevents high similarity scores for candidates with very few data points
// 	const baseNonZeroPoints = baseSeries.filter(p => Math.abs(p.total || 0) > 0).length;
// 	const candidateNonZeroPoints = candidateSeries.filter(p => Math.abs(p.total || 0) > 0).length;
	
// 	// Calculate data sparsity ratio
// 	if (baseNonZeroPoints > 0 && candidateNonZeroPoints > 0) {
// 		const sparsityRatio = candidateNonZeroPoints / baseNonZeroPoints;
		
// 		// If candidate has less than 50% of the data points of base, apply penalty
// 		if (sparsityRatio < 0.5) {
// 			// Penalty: reduce similarity proportionally to sparsity
// 			// Example: if candidate has 1 point and base has 10 points (10% ratio),
// 			// apply 90% penalty, reducing similarity significantly
// 			const penalty = (1 - sparsityRatio) * 0.7; // Max 70% penalty for very sparse data
// 			similarity = similarity * (1 - penalty);
// 		}
// 	}
	
// 	// Round to 2 decimal places to avoid floating point issues
// 	similarity = Math.max(0, Math.min(100, similarity));
	
// 	return parseFloat(similarity.toFixed(2));
// };

/**
 * INTERNAL: compute point-by-point similarity between two time series.
 * Returns a similarity score from 0-100, where 100 is identical.
 * Handles series of different lengths by aligning by time period (x value).
 */
const computePointByPointSimilarity = (baseSeries, candidateSeries) => {
	if (!Array.isArray(baseSeries) || !Array.isArray(candidateSeries) || baseSeries.length === 0 || candidateSeries.length === 0) {
		return 0;
	}

	// Create a map of candidate series by x value for quick lookup
	const candidateMap = new Map();
	candidateSeries.forEach((point) => {
		if (point.x) {
			candidateMap.set(String(point.x), point.total || 0);
		}
	});

	// Compare each point in base series with corresponding point in candidate series
	let totalDiff = 0;
	let totalBaseValue = 0;
	let matchedPoints = 0;
	
	baseSeries.forEach((basePoint) => {
		const baseValue = basePoint.total || 0;
		const candidateValue = candidateMap.get(String(basePoint.x)) || 0;

		// Calculate relative difference
		if (baseValue === 0 && candidateValue === 0) {
			// Both are zero - perfect match
			matchedPoints++;
		} else if (baseValue === 0 || candidateValue === 0) {
			// One is zero, other is not - significant difference
			const maxValue = Math.max(Math.abs(baseValue), Math.abs(candidateValue));
			totalDiff += maxValue;
			totalBaseValue += maxValue;
		} else {
			// Both non-zero - calculate relative difference
			const diff = Math.abs(baseValue - candidateValue);
			const avgValue = (Math.abs(baseValue) + Math.abs(candidateValue)) / 2;
			totalDiff += diff;
			totalBaseValue += avgValue;
		}
	});

	// Calculate similarity percentage
	if (totalBaseValue === 0) {
		// All values are zero - check if all points matched
		return baseSeries.length === matchedPoints ? 100 : 0;
	}

	const avgDiffPercent = (totalDiff / totalBaseValue) * 100;
	const similarity = Math.max(0, 100 - avgDiffPercent);
	return similarity;
};

/**
 * High-level local analysis for Trend Mapping (Hospital only for now).
 * Uses global hospital.patientList + dialog filter context; no API calls.
 */
export const analyzeTrendsLocally = async ({
	pageType,
	dialogFilters,
	config,
	baseCardType,
	patientsOverride,
	groupByType,
}) => {
	if (pageType !== "hospital" && pageType !== "communityTransfer" && pageType !== "deceased" && pageType !== "admission" && pageType !== "overall") {
		// For now we only support Hospital, Community Transfer, Deceased, Admission, and Overall; other pages can be added later.
		return { totalMatches: 0, matchesByCard: [] };
	}

	const getDashboardLabel = (type) => {
		switch (type) {
			case "communityTransfer": return "Community Transfer";
			case "deceased": return "Deceased";
			case "admission": return "Admission";
			case "overall": return "Overall";
			default: return "Hospital";
		}
	};

	const state = store.getState();
	// Get patient list based on page type
	let patientListObj;
	if (pageType === "communityTransfer") {
		patientListObj = state?.communityTransfer?.patientList || {};
	} else if (pageType === "deceased") {
		patientListObj = state?.deceased?.patientList || {};
	} else if (pageType === "admission") {
		patientListObj = state?.admission?.patientList || {};
	} else if (pageType === "overall") {
		patientListObj = state?.overall?.patientList || {};
	} else {
		patientListObj = state?.hospital?.patientList || {};
	}
	
	// CRITICAL: We need the FULL unfiltered patient list for analysis
	// patientsOverride is now defaultPatientData (full unfiltered list from HospitalCardDetailsDialog)
	// patientListObj.list might be empty or already filtered
	// Prefer patientsOverride if it has patients, otherwise fall back to Redux
	const reduxList = patientListObj.list || [];
	const overrideList = patientsOverride || [];
	
	// Prefer patientsOverride (defaultPatientData) if it has patients
	// This is the full unfiltered dataset passed from HospitalCardDetailsDialog
	const allPatientsSource = overrideList.length > 0 ? overrideList : reduxList;
	const allPatients = Array.isArray(allPatientsSource) ? allPatientsSource : [];
	const filter = dialogFilters?.filter;
	
	// Get dynamicCards for custom card filtering (page-specific)
	const dynamicCards = dialogFilters?.dynamicCards ||
		(pageType === "communityTransfer"
			? (state?.communityTransfer?.dynamicCards || [])
			: pageType === "deceased"
				? (state?.deceased?.dynamicCards || [])
				: pageType === "admission"
					? (state?.admission?.dynamicCards || [])
					: pageType === "overall"
						? (state?.overall?.dynamicCards || [])
						: (state?.hospital?.dynamicCards || []));
	// Get ninetyDaysData/sixtyDaysData for 90/60 days card filtering - try multiple sources (page-specific)
	// CRITICAL: We need the actual data array from patientListObj, not from filterListData
	// filterListData has card items for display, but the data array has the actual patient ID mappings
	// Different pages use different names: Hospital/Deceased/Overall use ninetyDaysData, Community/Admission use sixtyDaysData
	let ninetyDaysData = null;
	
	// Helper function to get data from multiple sources
	const getDaysDataFromSources = (dataName) => {
		// Try multiple sources in order of preference:
		// 1. dialogFilters[dataName] (passed from parent component)
		if (dialogFilters?.[dataName] && Array.isArray(dialogFilters[dataName]) && dialogFilters[dataName].length > 0) {
			return dialogFilters[dataName];
		}
		// 2. patientListObj[dataName] (from Redux state)
		if (patientListObj?.[dataName] && Array.isArray(patientListObj[dataName]) && patientListObj[dataName].length > 0) {
			return patientListObj[dataName];
		}
		// 3. dialogFilters.originalData[dataName] (from API response)
		if (dialogFilters?.originalData?.[dataName] && Array.isArray(dialogFilters.originalData[dataName]) && dialogFilters.originalData[dataName].length > 0) {
			return dialogFilters.originalData[dataName];
		}
		// 4. Redux state directly (page-specific)
		const stateData = pageType === "communityTransfer"
			? (state?.communityTransfer?.[dataName] || [])
			: pageType === "deceased"
				? (state?.deceased?.[dataName] || [])
				: pageType === "admission"
					? (state?.admission?.[dataName] || [])
					: pageType === "overall"
						? (state?.overall?.[dataName] || [])
						: (state?.hospital?.[dataName] || []);
		
		if (Array.isArray(stateData) && stateData.length > 0) {
			return stateData;
		}
		
		return null;
	};
	
	// Try to get data based on page type
	// Hospital, Deceased, Overall use ninetyDaysData
	// Community Transfer, Admission use sixtyDaysData
	if (pageType === "communityTransfer" || pageType === "admission") {
		// Try sixtyDaysData first
		ninetyDaysData = getDaysDataFromSources('sixtyDaysData');
		if (!ninetyDaysData) {
			// Fallback to ninetyDaysData (some pages might have both)
			ninetyDaysData = getDaysDataFromSources('ninetyDaysData');
		}
	} else {
		// Hospital, Deceased, Overall use ninetyDaysData
		ninetyDaysData = getDaysDataFromSources('ninetyDaysData');
	}
	
	// Last resort: Try to get from filterListData (but this might not have the full structure with 'ids' arrays)
	// NOTE: filterListData might have the display items but not the patient ID mappings
	if (!ninetyDaysData || !Array.isArray(ninetyDaysData) || ninetyDaysData.length === 0) {
		const filterListData = dialogFilters?.filterListData || {};
		// Try all possible card type keys
		const filterListDaysData = filterListData[HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA] || 
			filterListData[CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA] ||
			filterListData[DECEASED_CARDS_TYPE.NINETY_DAYS_DATA] ||
			filterListData[OVERALL_CARDS_TYPE.NINETY_DAYS_DATA] ||
			filterListData[ADMISSION_CARDS_TYPE.SIXTY_DAYS_DATA] ||
			filterListData['ninetyDaysData'] ||
			filterListData['sixtyDaysData'] ||
			[];
		
		if (Array.isArray(filterListDaysData) && filterListDaysData.length > 0) {
			// Check if items have 'ids' property (the structure we need)
			const hasIdsProperty = filterListDaysData.some(item => Array.isArray(item.ids));
			if (hasIdsProperty) {
				ninetyDaysData = filterListDaysData;
			} else {
				console.warn(`[analyzeTrendsLocally] filterListData has days data items but they lack "ids" property - cannot use for filtering (${pageType})`);
				ninetyDaysData = [];
			}
		} else {
			ninetyDaysData = [];
			console.warn(`[analyzeTrendsLocally] WARNING: Days data (ninetyDaysData/sixtyDaysData) not found in any source for ${pageType}!`);
		}
	}

	if (!filter || !filter.startDate || !filter.endDate || !allPatients.length) {
		return { totalMatches: 0, matchesByCard: [] };
	}

	const selectedCards = config.selectedCards || [];
	const selectedItemsByCard = config.selectedItemsByCard || {};
	const groupsByCard = config.groupsByCard || {};
	const crossDashboardSelections = Array.isArray(config?.crossDashboardSelections)
		? config.crossDashboardSelections
		: [];
	const filterListData = dialogFilters?.filterListData || {};
	const similarityTolerance =
		typeof config.similarityTolerance === "number"
			? config.similarityTolerance
			: 10; // default ±10%
	
	// CRITICAL: Extract isolated date ranges from config
	// Only apply isolation if user has explicitly selected ranges (clicked "Isolate" and selected ranges)
	// If no ranges are provided, analysis works normally without any isolation filtering
	// Multiple ranges are handled as union (any point within any range is included)
	const isolatedDateRanges = Array.isArray(config.isolatedDateRanges) && config.isolatedDateRanges.length > 0
		? config.isolatedDateRanges
		: [];
	const currentFilterType = isolatedDateRanges.length > 0
		? (config.currentFilterType || groupByType || CHART_FILTER_DAY_OF.DAY)
		: null; // Only needed when isolation is active

	// Determine filter mode and which filters to apply
	const filterMode = config.filterMode || "keepAll"; // "keepAll", "keepSome", "removeAll"
	const cardFilter = dialogFilters?.cardFilter || {};
	// CRITICAL: For admission page, use mainPriorityData instead of priorityData
	// Other pages use priorityData, but admission page uses mainPriorityData
	const priorityData = pageType === "admission"
		? (dialogFilters?.cardFilter?.mainPriorityData || [])
		: (dialogFilters?.cardFilter?.priorityData || []);
	const selectedFilterCards = config.selectedFilterCards || []; // For "keepSome" mode

	// CRITICAL FIX: ChartDialogLeftSidebar filters are stored in cardFilter[baseCardType].
	// Historically we excluded baseCardType from cardFilter so that its item selection
	// (selectedItemsByCard[baseCardType]) controlled the base trend without double-filtering.
	//
	// However, for "Keep Some Filters" mode the user explicitly chooses which filters
	// to keep. If they choose ONLY the base card filter, we MUST respect that choice
	// and apply the baseCardType filter as well.
	//
	// New behaviour:
	// - "keepAll"      → always exclude baseCardType from cardFilter (same as before)
	// - "keepSome"     → exclude baseCardType ONLY when it is NOT in selectedFilterCards
	// - "removeAll"    → cardFilter is ignored by caller, this setting is effectively unused
	// Special filtering cards (like ninetyDaysData/sixtyDaysData) should NOT be excluded from global filters
	// even when they're the base card, because they filter the global patient list
	const specialFilteringCards = [
		HOSPITAL_CARDS_TYPE.NINETY_DAYS_DATA, 
		CO_TRANSFER_CARDS_TYPE.SIXTY_DAYS_DATA, 
		DECEASED_CARDS_TYPE.NINETY_DAYS_DATA, 
		OVERALL_CARDS_TYPE.NINETY_DAYS_DATA,
		ADMISSION_CARDS_TYPE.SIXTY_DAYS_DATA
	];
	const isSpecialFilteringCard = baseCardType && specialFilteringCards.includes(baseCardType);
	
	let excludeBaseCardFromCardFilter = [];
	if (baseCardType && baseCardType !== "priorityData" && baseCardType !== "mainPriorityData") {
		const baseCardIsKeptInSomeMode =
			filterMode === "keepSome" && selectedFilterCards.includes(baseCardType);

		// Check if base card is in priorityData/mainPriorityData
		// If it is, we need to include it in cardFilter so priorityData processing can find it
		const isBaseCardInPriorityData = priorityData && Array.isArray(priorityData) && priorityData.some((ele) => ele && ele.type === baseCardType);
		const isBaseCardInMainPriorityData = pageType === "admission" && dialogFilters?.cardFilter?.mainPriorityData && Array.isArray(dialogFilters.cardFilter.mainPriorityData) && dialogFilters.cardFilter.mainPriorityData.some((ele) => ele && ele.type === baseCardType);

		// Exclude base card from global filters unless:
		// 1. The user explicitly kept it in "keepSome" mode, OR
		// 2. It's a special filtering card (like ninetyDaysData) that needs to filter the global patient list, OR
		// 3. It's in priorityData/mainPriorityData (needs to be processed in priority order)
		if ((filterMode === "keepAll" || !baseCardIsKeptInSomeMode) && !isSpecialFilteringCard && !isBaseCardInPriorityData && !isBaseCardInMainPriorityData) {
			excludeBaseCardFromCardFilter = [baseCardType];
		}
	}

	// CRITICAL: Apply transferType filter FIRST (before card filters)

	// CRITICAL: Apply transferType filter FIRST (before card filters)
	// transferType is a top-level view mode filter, not a card filter
	// It should be applied regardless of filterMode (keepAll, keepSome, removeAll)
	// because it's a view selection, not a data filter
	let patientsAfterTransferType = allPatients;
	const transferType = dialogFilters?.transferType;
	
	if (transferType && pageType === "hospital") {
		// Hospital page: transferType is a single value (TYPES.PLANNED, TYPES.UNPLANNED, or null)
		if (transferType === TYPES.PLANNED) {
			patientsAfterTransferType = _.filter(patientsAfterTransferType, {
				transferType: "plannedHospitalTransfer",
			});
		} else if (transferType === TYPES.UNPLANNED) {
			patientsAfterTransferType = _.filter(patientsAfterTransferType, {
				transferType: "unplannedHospitalTransfer",
			});
		}
		// TYPES.ALL or null means no transferType filter (show all)
	} else if (transferType && pageType === "communityTransfer") {
		// Community Transfer page: transferType is an array that can contain multiple values
		// [TRANSFER_TYPE.SAFE_DISCHARGE, TRANSFER_TYPE.SNF, TRANSFER_TYPE.AMA, TRANSFER_TYPE.ALL]
		if (Array.isArray(transferType) && transferType.length > 0) {
			// If TRANSFER_TYPE.ALL is in the array, don't filter (show all patients)
			if (!_.includes(transferType, TRANSFER_TYPE.ALL)) {
				// Filter to only patients whose transferType is in the selected array
				patientsAfterTransferType = _.filter(patientsAfterTransferType, (item) =>
					_.includes(transferType, item.transferType)
				);
			}
			// If TRANSFER_TYPE.ALL is included, patientsAfterTransferType remains as allPatients (no filtering)
		}
	} else if (transferType && pageType === "admission") {
		// Admission page: transferType is a single value (ADMISSION_TYPES.ADMISSION, ADMISSION_TYPES.READMISSION, or null)
		// For Admission page, we filter by the patient's `type` field (not `transferType` field)
		if (transferType === ADMISSION_TYPES.ADMISSION) {
			patientsAfterTransferType = _.filter(patientsAfterTransferType, {
				type: "admission",
			});
		} else if (transferType === ADMISSION_TYPES.READMISSION) {
			patientsAfterTransferType = _.filter(patientsAfterTransferType, {
				type: "readmission",
			});
		}
		// ADMISSION_TYPES.ALL or null means no transferType filter (show all)
	} else if (transferType && pageType === "overall") {
		// Overall page: transferType is a single value (OVERALL_PAGE_SUB_TYPE.TOTAL_INCOMING, OVERALL_PAGE_SUB_TYPE.TOTAL_OUTGOING, or null)
		// For Overall page, we filter by the patient's `isOutgoing` field
		if (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_INCOMING) {
			patientsAfterTransferType = _.filter(patientsAfterTransferType, {
				isOutgoing: false,
			});
		} else if (transferType === OVERALL_PAGE_SUB_TYPE.TOTAL_OUTGOING) {
			patientsAfterTransferType = _.filter(patientsAfterTransferType, {
				isOutgoing: true,
			});
		}
		// OVERALL_PAGE_SUB_TYPE.TOTAL or null means no transferType filter (show all)
	}

	// CRITICAL: In "keepAll" mode, we should ONLY use the original cardFilter from the dashboard
	// We should NOT merge selectedItemsByCard into cardFilter because:
	// 1. selectedItemsByCard contains selections from the Trend Mapping dialog UI
	// 2. These selections are meant for building candidate series, NOT for filtering the global patient list
	// 3. The original cardFilter already contains the correct dashboard filters that the user wants to keep
	// 
	// Exception: Special filtering cards (like ninetyDaysData) might need to be merged if they're
	// selected in the dialog but not in the original cardFilter
	const mergedCardFilter = { ...cardFilter };
	
	if (filterMode === "keepAll") {
		// In keepAll mode, use original cardFilter only
		// Only merge special filtering cards if they're in selectedItemsByCard but not in cardFilter
		// This handles the case where user selects a special filtering card in the dialog
		specialFilteringCards.forEach((cardType) => {
			const selectedItems = selectedItemsByCard[cardType];
			const existingFilter = mergedCardFilter[cardType];
			
			// Only merge if:
			// 1. There are selected items in the dialog
			// 2. AND there's no existing filter in cardFilter (or it's empty)
			if (Array.isArray(selectedItems) && selectedItems.length > 0) {
				if (!existingFilter || !Array.isArray(existingFilter) || existingFilter.length === 0) {
					// No existing filter, use the selected items from dialog
					mergedCardFilter[cardType] = [...selectedItems];
				}
				// If existingFilter exists, keep it (don't override dashboard filters)
			}
		});
	} else {
		// For keepSome and removeAll modes, merge selectedItemsByCard for non-base cards
		Object.keys(selectedItemsByCard).forEach((cardType) => {
			// Check if this cardType is a special filtering card
			const isThisCardSpecial = specialFilteringCards.includes(cardType);
			
			// Don't merge baseCardType UNLESS it's a special filtering card (like ninetyDaysData)
			// Special filtering cards need to be applied to the global patient list even when they're the base card
			if (cardType === baseCardType && !isThisCardSpecial) {
				return;
			}
			
			const selectedItems = selectedItemsByCard[cardType];
			if (Array.isArray(selectedItems) && selectedItems.length > 0) {
				const existingFilter = mergedCardFilter[cardType] || [];
				// Merge: combine existing filter with selectedItems, removing duplicates
				const merged = [...new Set([...existingFilter, ...selectedItems])];
				mergedCardFilter[cardType] = merged;
			}
		});
	}
	
	// CRITICAL: Ensure special filtering cards from original cardFilter are preserved
	// even if they're not in selectedItemsByCard (user might have selected them in the dialog UI)
	specialFilteringCards.forEach((specialCardType) => {
		// If this special card is in the original cardFilter but not in mergedCardFilter, preserve it
		if (cardFilter[specialCardType] && Array.isArray(cardFilter[specialCardType]) && cardFilter[specialCardType].length > 0) {
			if (!mergedCardFilter[specialCardType] || mergedCardFilter[specialCardType].length === 0) {
				// Preserve the original filter if no selection was made in selectedItemsByCard
				mergedCardFilter[specialCardType] = cardFilter[specialCardType];
			} else {
				// Merge with existing selection
				const merged = [...new Set([...cardFilter[specialCardType], ...(mergedCardFilter[specialCardType] || [])])];
				mergedCardFilter[specialCardType] = merged;
			}
		}
	});
	
	// CRITICAL: If baseCardType is in priorityData, we need to include it in priorityDataForFiltering
	// so it gets applied to filteredAllPatients (for other cards in the analysis)
	// However, we should NOT include filters AFTER the base card in priorityData
	// This ensures that filters AFTER the current card in priorityData are NOT applied
	// This matches the behavior in hospital-common.js where filters after current card are skipped
	// CRITICAL: If the base card is NOT in priorityData (because it was trimmed in HospitalCardDetailsDialog),
	// we need to include it in priorityDataForFiltering AND mergedCardFilter so it gets applied to filteredAllPatients
	// This applies to both special filtering cards (like ninetyDaysData) and regular cards (like DCERData)
	let priorityDataForFiltering = priorityData;
	if (baseCardType && priorityData && Array.isArray(priorityData) && priorityData.length > 0) {
		const baseCardIndex = priorityData.findIndex((ele) => ele && ele.type === baseCardType);
		if (baseCardIndex >= 0) {
			// Base card is in priorityData - include filters BEFORE it AND the base card itself
			// This ensures the base card's filter is applied to filteredAllPatients
			// (so other cards in the analysis show the correct filtered count)
			priorityDataForFiltering = priorityData.slice(0, baseCardIndex + 1);
		} else {
			// Base card is NOT in priorityData (likely because it was trimmed in HospitalCardDetailsDialog)
			// We need to include it in priorityDataForFiltering AND mergedCardFilter so it gets applied to filteredAllPatients
			// Check if base card has filters in cardFilter
			const baseCardFilters = cardFilter[baseCardType];
			if (baseCardFilters && Array.isArray(baseCardFilters) && baseCardFilters.length > 0) {
				// Create a copy of priorityData and add the base card at the end
				// This preserves the order: filters before base card, then base card itself
				priorityDataForFiltering = [...priorityData];
				priorityDataForFiltering.push({
					type: baseCardType,
					items: baseCardFilters,
					selectedItems: baseCardFilters
				});
				
				// CRITICAL: Also ensure the base card's filter is in mergedCardFilter
				// so applyCardFiltersToPatients can find it when processing priorityDataForFiltering
				mergedCardFilter[baseCardType] = baseCardFilters;
				
				// CRITICAL: Remove base card from excludeBaseCardFromCardFilter
				// so it doesn't get excluded when applying filters to filteredAllPatients
				excludeBaseCardFromCardFilter = excludeBaseCardFromCardFilter.filter(cardType => cardType !== baseCardType);
			} else {
				// No filters for base card, just use priorityData as-is
				priorityDataForFiltering = priorityData;
			}
		}
	} else if (baseCardType) {
		// No priorityData at all, but base card exists
		// Check if base card has filters in cardFilter
		const baseCardFilters = cardFilter[baseCardType];
		if (baseCardFilters && Array.isArray(baseCardFilters) && baseCardFilters.length > 0) {
			priorityDataForFiltering = [{
				type: baseCardType,
				items: baseCardFilters,
				selectedItems: baseCardFilters
			}];
			
			// CRITICAL: Also ensure the base card's filter is in mergedCardFilter
			mergedCardFilter[baseCardType] = baseCardFilters;
			
			// CRITICAL: Remove base card from excludeBaseCardFromCardFilter
			// so it doesn't get excluded when applying filters to filteredAllPatients
			excludeBaseCardFromCardFilter = excludeBaseCardFromCardFilter.filter(cardType => cardType !== baseCardType);
		}
	}
	
	// Apply filters to patientsAfterTransferType based on filter mode
	let filteredAllPatients = patientsAfterTransferType;
	
	if (filterMode === "keepAll") {
		// Apply all existing filters EXCEPT baseCardType (its filters come from selectedItemsByCard)
		filteredAllPatients = applyCardFiltersToPatients(
			patientsAfterTransferType,
			mergedCardFilter, // Use mergedCardFilter instead of cardFilter
			priorityDataForFiltering, // Use trimmed priorityData (only filters before base card)
			excludeBaseCardFromCardFilter, // Exclude baseCardType from cardFilter
			dynamicCards,
			ninetyDaysData, // Pass ninetyDaysData for 90 days card filtering
			filterListData // Pass filterListData for key-to-ID conversion
		);
	} else if (filterMode === "keepSome") {
		// Special case: no cards selected in "Keep Some Filters" UI
		// -> behave like "Remove All Filters" (do not apply ANY dashboard filters)
		if (!selectedFilterCards || selectedFilterCards.length === 0) {
			filteredAllPatients = patientsAfterTransferType;
		} else {
			// Apply only selected filters (exclude cards not in selectedFilterCards AND baseCardType)
			const excludeCards = [
				...Object.keys(cardFilter).filter(
					(cardType) => !selectedFilterCards.includes(cardType) && cardType !== 'priorityData' && cardType !== 'mainPriorityData'
				),
				...excludeBaseCardFromCardFilter
			];
			// Remove duplicates
			const uniqueExcludeCards = [...new Set(excludeCards)];

			// Build trimmed priorityData for keepSome mode (only filters before base card)
			let trimmedPriorityDataForKeepSome = priorityDataForFiltering;
			if (priorityDataForFiltering && Array.isArray(priorityDataForFiltering) && priorityDataForFiltering.length > 0) {
				// Only include selected filter cards that are in the trimmed priorityData
				trimmedPriorityDataForKeepSome = priorityDataForFiltering.filter((ele) => 
					ele && selectedFilterCards.includes(ele.type)
				);
			}
			
			filteredAllPatients = applyCardFiltersToPatients(
				patientsAfterTransferType,
				mergedCardFilter, // Use mergedCardFilter instead of cardFilter
				trimmedPriorityDataForKeepSome, // Use trimmed priorityData (only filters before base card that are selected)
				uniqueExcludeCards,
				dynamicCards,
				ninetyDaysData, // Pass ninetyDaysData for 90 days card filtering
				filterListData // Pass filterListData for key-to-ID conversion
			);
		}
	} else {
		// filterMode === "removeAll" - use patientsAfterTransferType as-is (no card filtering, but transferType still applies)
		filteredAllPatients = patientsAfterTransferType;
	}

	// Merge dynamicCards and customTabs (same approach as TrendMappingDialog)
	// Do this once before processing cards for efficiency
	const customTabs = patientListObj?.customTabs || [];
	const allDynamicCards = [...(dynamicCards || []), ...(customTabs || [])];
	
	// 1) Build base series from current card
	// CRITICAL: The main card chart shows the TOTAL for all patients in that card
	// We need to apply filters in the SAME order as hospital-common.js:
	// 1. transferType (already applied to patientsAfterTransferType)
	// 2. priorityData filters that come BEFORE the base card (in priority order)
	// 3. Base card's own filters (if any)
	// This ensures the base series matches what's shown in the main card chart
	let basePatients = patientsAfterTransferType;
	
	// Get base card filters from cardFilter (dashboard filters) - this is what the main card chart uses
	// CRITICAL: The current card's filter comes from selectedItem in the left sidebar (ChartDialogLeftSidebar)
	// This is set in HospitalCardDetailsDialog.js when the current card is in priorityData
	// For special filtering cards in priorityData, the filter might be in the priorityData entry
	// or in cardFilter[baseCardType]. We need to check both sources.
	// CRITICAL: Always prioritize cardFilter[baseCardType] as it contains the selectedItem from left sidebar
	let baseCardFilters = cardFilter[baseCardType];
	
	// If baseCardFilters is empty, try to get from priorityData entry (fallback)
	// This handles cases where the filter might be stored in the priorityData entry structure
	if ((!baseCardFilters || !Array.isArray(baseCardFilters) || baseCardFilters.length === 0) && 
		baseCardType && priorityData && Array.isArray(priorityData)) {
		const baseCardEntry = priorityData.find(ele => ele && ele.type === baseCardType);
		if (baseCardEntry && (baseCardEntry.items || baseCardEntry.selectedItems)) {
			const items = baseCardEntry.items || baseCardEntry.selectedItems || [];
			if (Array.isArray(items) && items.length > 0) {
				baseCardFilters = items;
			}
		}
	}
	
	// CRITICAL: Check if base card is in priorityData to determine which filters to apply
	// If base card is in priorityData, only apply filters BEFORE it in priority order
	// If base card is NOT in priorityData, apply ALL priorityData filters
	const baseCardIndexInPriority = priorityData && Array.isArray(priorityData) 
		? priorityData.findIndex((ele) => ele && ele.type === baseCardType)
		: -1;
	
	// Build cardFilter for base card that includes:
	// 1. All priorityData filters BEFORE the base card (if base card is in priorityData)
	// 2. Base card's own filters (if any)
	// CRITICAL: For special filtering cards (like ninetyDaysData) that are the base card,
	// we MUST include them in baseCardFilterForAnalysis so their filter gets applied
	const baseCardFilterForAnalysis = {};
	const isBaseCardSpecialFiltering = specialFilteringCards.includes(baseCardType);
	
	if (baseCardIndexInPriority >= 0) {
		// Base card is in priorityData - only include filters BEFORE it
		for (let i = 0; i < baseCardIndexInPriority; i++) {
			const ele = priorityData[i];
			if (ele && ele.type) {
				const filterValue = cardFilter[ele.type];
				if (filterValue && Array.isArray(filterValue) && filterValue.length > 0) {
					baseCardFilterForAnalysis[ele.type] = filterValue;
				}
			}
		}
		// CRITICAL: Always include base card's own filters if they exist
		// This is especially important for special filtering cards like ninetyDaysData
		// The filter comes from selectedItem in the left sidebar (set in HospitalCardDetailsDialog.js)
		if (Array.isArray(baseCardFilters) && baseCardFilters.length > 0) {
			baseCardFilterForAnalysis[baseCardType] = baseCardFilters;
		} else if (isBaseCardSpecialFiltering) {
			// For special filtering cards, if baseCardFilters is empty, try to get from priorityData entry
			const baseCardEntry = priorityData[baseCardIndexInPriority];
			if (baseCardEntry && (baseCardEntry.items || baseCardEntry.selectedItems)) {
				const items = baseCardEntry.items || baseCardEntry.selectedItems || [];
				if (Array.isArray(items) && items.length > 0) {
					baseCardFilterForAnalysis[baseCardType] = items;
				}
			}
		}
	} else {
		// Base card is NOT in priorityData - include ALL priorityData filters
		if (priorityData && Array.isArray(priorityData)) {
			for (const ele of priorityData) {
				if (ele && ele.type) {
					const filterValue = cardFilter[ele.type];
					if (filterValue && Array.isArray(filterValue) && filterValue.length > 0) {
						baseCardFilterForAnalysis[ele.type] = filterValue;
					}
				}
			}
		}
		// Also include base card's own filters if they exist
		if (Array.isArray(baseCardFilters) && baseCardFilters.length > 0) {
			baseCardFilterForAnalysis[baseCardType] = baseCardFilters;
		}
	}
	
	// Apply filters to get patients that match the main card chart
	// This includes priorityData filters (before base card) + base card's own filters
	if (Object.keys(baseCardFilterForAnalysis).length > 0) {
		// Build priorityData array for base card filtering
		// CRITICAL: For special filtering cards (like ninetyDaysData) that are the base card,
		// we need to include them in the priorityData array so they get processed correctly
		// For regular cards, we only include filters before the base card
		let baseCardPriorityData;
		if (baseCardIndexInPriority >= 0) {
			// Base card is in priorityData
			// CRITICAL: Always include the base card in baseCardPriorityData so it gets processed in the correct order
			// This ensures: 1) Filters BEFORE base card are applied first, 2) Then base card's filter (from selectedItem) is applied
			// This matches the behavior in hospital-common.js where filters are applied in priorityData order
			// This works for both special filtering cards and regular cards, and all pages
			baseCardPriorityData = priorityData.slice(0, baseCardIndexInPriority + 1);
			
			// CRITICAL: Update the base card entry in baseCardPriorityData to use the filter from selectedItem (left sidebar)
			// This ensures the current card's filter from ChartDialogLeftSidebar is applied, not the filter from the original priorityData
			// The filter from selectedItem is stored in cardFilter[baseCardType] which is retrieved as baseCardFilters
			if (baseCardFilterForAnalysis[baseCardType] && Array.isArray(baseCardFilterForAnalysis[baseCardType]) && baseCardFilterForAnalysis[baseCardType].length > 0) {
				// Update the last entry (which should be the base card) to use the filter from selectedItem
				const baseCardEntryIndex = baseCardPriorityData.length - 1;
				if (baseCardEntryIndex >= 0 && baseCardPriorityData[baseCardEntryIndex] && baseCardPriorityData[baseCardEntryIndex].type === baseCardType) {
					baseCardPriorityData[baseCardEntryIndex] = {
						...baseCardPriorityData[baseCardEntryIndex],
						items: baseCardFilterForAnalysis[baseCardType],
						selectedItems: baseCardFilterForAnalysis[baseCardType]
					};
				}
			}
		} else {
			// Base card is NOT in priorityData (likely because it was trimmed in HospitalCardDetailsDialog OR it's not in priorityData)
			// Include all priorityData filters
			baseCardPriorityData = priorityData ? [...priorityData] : [];
			
			// CRITICAL: If base card has filters in baseCardFilterForAnalysis, we MUST add it to baseCardPriorityData
			// This ensures it gets processed in the correct order (after filters before it)
			// This works for both special filtering cards (like ninetyDaysData) and regular cards (like DCERData, hospitalizations)
			// This handles the case where the current card was trimmed from priorityData in HospitalCardDetailsDialog
			// OR when the current card is NOT in priorityData but has a filter from selectedItem (left sidebar)
			if (baseCardFilterForAnalysis[baseCardType] && Array.isArray(baseCardFilterForAnalysis[baseCardType]) && baseCardFilterForAnalysis[baseCardType].length > 0) {
				// Create a mock priorityData entry for the base card so it gets processed
				baseCardPriorityData.push({
					type: baseCardType,
					items: baseCardFilterForAnalysis[baseCardType],
					selectedItems: baseCardFilterForAnalysis[baseCardType]
				});
			}
		}
		
		basePatients = applyCardFiltersToPatients(
			patientsAfterTransferType, // Start with all patients (after transferType filter)
			baseCardFilterForAnalysis, // Apply priorityData filters (before base) + base card filters
			baseCardPriorityData, // Process priorityData filters (includes base card if it's special filtering)
			[], // Don't exclude any cards
			dynamicCards,
			ninetyDaysData,
			filterListData
		);
	}
	// If no filters to apply, use patientsAfterTransferType as-is
	// This means the base card shows all patients (no filtering)

	// De-duplicate base patients by _id
	basePatients = _.uniqBy(basePatients, (p) => String(p._id || p.id || ""));

	// Add census data to filter for accurate monthly calculations (matching main chart)
	// This ensures chartGroupBy uses the same census values as the main chart
	const filterWithCensus = {
		...filter,
		censusAverage: dialogFilters?.censusAverage,
		censusByPeriod: dialogFilters?.censusByPeriod || [],
	};

	const baseSeriesOverride = Array.isArray(config?.baseSeriesOverride)
		? config.baseSeriesOverride
		: null;
	let baseSeries = baseSeriesOverride
		? [...baseSeriesOverride]
		: await buildSeriesForGroupType(
			basePatients,
			filterWithCensus,
			groupByType,
			pageType,
			transferType
		);
	if (!baseSeries.length) {
		return { totalMatches: 0, matchesByCard: [] };
	}

	// CRITICAL: Filter base series by isolated date ranges if provided
	// This ensures analysis only considers data from the selected date ranges
	// The filtering happens BEFORE percentage calculation, so percentages are based on isolated data only
	// This works correctly for percentageBased mode: both base and candidate series are filtered first,
	// then aligned and converted to percentages for comparison
	if (isolatedDateRanges.length > 0) {
		baseSeries = filterSeriesByIsolatedRanges(baseSeries, isolatedDateRanges, currentFilterType);
		if (!baseSeries.length) {
			// If filtering removes all points, return no matches
			return { totalMatches: 0, matchesByCard: [] };
		}
	}
	
	const baseSlope =
		typeof config?.baseSlopeOverride === "number"
			? config.baseSlopeOverride
			: computeSlope(baseSeries);
	
	// Build lookup maps for faster item label resolution
	const buildItemLabelLookup = () => {
		const lookup = {};
		const filterListData = dialogFilters?.filterListData || {};

		Object.keys(filterListData).forEach((cardId) => {
			const cardItems = filterListData[cardId];
			if (Array.isArray(cardItems)) {
				lookup[cardId] = {};
				cardItems.forEach((item) => {
					// Get all possible identifiers and labels
					const id = item._id || item.id;
					const key = item.key;
					const value = item.value;
					const name = item.name || item.label || item.title || "";

					// Index by all possible identifier formats
					if (id) {
						lookup[cardId][String(id)] = name || String(id);
					}
					if (key) {
						lookup[cardId][String(key)] = name || String(key);
					}
					if (value) {
						lookup[cardId][String(value)] = name || String(value);
					}
					// Also index by name/label for cards where itemId is the label itself
					if (name) {
						lookup[cardId][name] = name;
						// Also try case-insensitive match
						lookup[cardId][name.toLowerCase()] = name;
					}
				});

			}
		});

		return lookup;
	};

	const itemLabelLookup = buildItemLabelLookup();

	// Helper: Get item label from filterListData (handles all identifier formats)
	const getItemLabel = (cardId, itemId) => {
		const itemIdStr = String(itemId || "");

		// For total/header cards, use their corresponding card labels from labels maps
		if (
			cardId === "total" ||
			cardId === "unplannedHospitalTransfer" ||
			cardId === "plannedHospitalTransfer" ||
			cardId === "AMA" ||
			cardId === "SNF" ||
			cardId === "safeDischarge" ||
			cardId === "admission" ||
			cardId === "readmission"
		) {
			if (pageType === "communityTransfer") {
				if (COMMUNITY_CARD_LABELS?.[cardId]) return COMMUNITY_CARD_LABELS[cardId];
			} else if (pageType === "admission") {
				if (ADMISSION_CARDS_LABELS?.[cardId]) return ADMISSION_CARDS_LABELS[cardId];
			} else if (pageType === "deceased") {
				if (DECEASED_CARDS_LABELS?.[cardId]) return DECEASED_CARDS_LABELS[cardId];
			} else {
				if (HOSPITAL_CARDS_LABELS?.[cardId]) return HOSPITAL_CARDS_LABELS[cardId];
			}
		}

		// First try the lookup map (exact match)
		if (itemLabelLookup[cardId] && itemLabelLookup[cardId][itemIdStr]) {
			return itemLabelLookup[cardId][itemIdStr];
		}

		// Try case-insensitive match
		if (itemLabelLookup[cardId] && itemLabelLookup[cardId][itemIdStr.toLowerCase()]) {
			return itemLabelLookup[cardId][itemIdStr.toLowerCase()];
		}

		// Last resort: try direct lookup in filterListData as fallback
		const cardItems = dialogFilters?.filterListData?.[cardId];
		if (Array.isArray(cardItems)) {
			const item = cardItems.find((i) => {
				return (
					String(i._id || "") === itemIdStr ||
					String(i.id || "") === itemIdStr ||
					String(i.key || "") === itemIdStr ||
					String(i.value || "") === itemIdStr ||
					String(i.name || "") === itemIdStr ||
					String(i.label || "") === itemIdStr ||
					// Case-insensitive match
					String(i.name || "").toLowerCase() === itemIdStr.toLowerCase() ||
					String(i.label || "").toLowerCase() === itemIdStr.toLowerCase()
				);
			});
			if (item) {
				return item.name || item.label || item.title || item.value || item.key || itemIdStr;
			}
		}

		// Fallback: for some card types (daysData, shiftData), itemId IS the label
		const isObjectIdFormat = /^[0-9a-f]{24}$/i.test(itemIdStr);
		if (!isObjectIdFormat && itemIdStr.length < 50) {
			// It's likely already a label (like "Mon", "Evening", "Option A")
			return itemIdStr;
		}

		return itemIdStr;
	};

	// Helper: Get card label (supports custom cards with same priority as DynamicCardItem)
	const getCardLabel = (cardId) => {
		// First check standard cards based on page type
		if (pageType === "communityTransfer") {
			if (COMMUNITY_CARD_LABELS?.[cardId]) {
				return COMMUNITY_CARD_LABELS[cardId];
			}
		} else if (pageType === "admission") {
			if (ADMISSION_CARDS_LABELS?.[cardId]) {
				return ADMISSION_CARDS_LABELS[cardId];
			}
		} else if (pageType === "overall") {
			if (OVERALL_CARDS_LABELS?.[cardId]) {
				return OVERALL_CARDS_LABELS[cardId];
			}
		} else {
			if (HOSPITAL_CARDS_LABELS?.[cardId]) {
				return HOSPITAL_CARDS_LABELS[cardId];
			}
		}

		// Check if it's a custom card - try multiple matching strategies
		const getTitleValue = (val) => {
			if (!val) return null;
			if (typeof val === 'string') {
				const trimmed = val.trim();
				return trimmed.length > 0 ? trimmed : null;
			}
			return val;
		};

		// Merge dynamicCards and customTabs (same approach as TrendMappingDialog)
		// Use correct page type for patientListObj
		const state = store.getState();
		const patientListObj = pageType === "communityTransfer"
			? (state?.communityTransfer?.patientList || {})
			: pageType === "admission"
				? (state?.admission?.patientList || {})
				: pageType === "overall"
					? (state?.overall?.patientList || {})
					: (state?.hospital?.patientList || {});
		const customTabs = patientListObj?.customTabs || [];
		const allDynamicCards = [...(dynamicCards || []), ...(customTabs || [])];

		// Try exact accessor match first
		const cardIdWithoutSuffix = cardId.replace(/Customtab$/i, '');
		let customCardDef = allDynamicCards.find((c) => c.accessor === cardId);
		
		// If not found, try alternative matching strategies
		if (!customCardDef) {
			customCardDef = allDynamicCards.find((c) => c.accessor === cardIdWithoutSuffix) ||
				allDynamicCards.find((c) => c.key === cardId) ||
				allDynamicCards.find((c) => c.key === cardIdWithoutSuffix) ||
				allDynamicCards.find((c) => String(c.id || '') === String(cardId)) ||
				allDynamicCards.find((c) => String(c._id || '') === String(cardId)) ||
				allDynamicCards.find((c) => c.accessor && String(c.accessor).toLowerCase() === String(cardId).toLowerCase()) ||
				allDynamicCards.find((c) => c.accessor && String(c.accessor).toLowerCase() === String(cardIdWithoutSuffix).toLowerCase()) ||
				allDynamicCards.find((c) => c.accessor && (String(cardId).includes(String(c.accessor)) || String(c.accessor).includes(String(cardId))));
		}

		if (customCardDef) {
			// Use same title priority as DynamicCardItem: title || label || tableLabel
			// Also check question.label and question.title as fallbacks
			const titleValue = getTitleValue(customCardDef?.title);
			const labelValue = getTitleValue(customCardDef?.label);
			const tableLabelValue = getTitleValue(customCardDef?.tableLabel);
			const questionLabelValue = getTitleValue(customCardDef?.question?.label);
			const questionTitleValue = getTitleValue(customCardDef?.question?.title);
			const questionQuestionValue = getTitleValue(customCardDef?.question?.question);
			const descriptionValue = getTitleValue(customCardDef?.description);
			
			const cardLabel = titleValue ||
				labelValue ||
				tableLabelValue ||
				questionLabelValue ||
				questionTitleValue ||
				questionQuestionValue ||
				descriptionValue;
			
			if (cardLabel) {
				return cardLabel;
			}
		}

		// Fallback: format the cardId to be more readable
		const formattedCardId = cardId
			.replace(/Customtab$/i, '')
			.replace(/([A-Z])/g, ' $1')
			.replace(/^./, str => str.toUpperCase())
			.trim();
		
		return formattedCardId || cardId;
	};

	// 2) For each selected card/item, build its series and compare
	const matchesByCard = [];

	if (selectedCards.length === 0 && crossDashboardSelections.length === 0) {
		return { totalMatches: 0, matchesByCard: [] };
	}

	for (const cardId of selectedCards) {
		// Check if this is a combineTab that should be excluded
		const cardIdWithoutSuffix = cardId.replace(/Customtab$/i, '');
		const customCardDef = allDynamicCards.find((c) => 
			c.accessor === cardId ||
			c.accessor === cardIdWithoutSuffix ||
			c.key === cardId ||
			c.key === cardIdWithoutSuffix ||
			String(c.id || '') === String(cardId) ||
			String(c._id || '') === String(cardId)
		);
		
		if (customCardDef) {
			// Check if this is a combineTab card (same logic as TrendMappingDialog)
			const cardType = customCardDef?.type;
			const questionType = customCardDef?.question?.type;
			const isCombineTab = cardType === CUSTOM_TAB_TYPE.combineTab || questionType === CUSTOM_TAB_TYPE.combineTab;
			
			if (isCombineTab) {
				// Check isCustomDateAnalysis and customAnalysisType at root level or in question
				const isCustomDateAnalysis = customCardDef?.isCustomDateAnalysis || customCardDef?.question?.isCustomDateAnalysis;
				const customAnalysisType = customCardDef?.customAnalysisType || customCardDef?.question?.customAnalysisType;
				const isValidCombineTab = isCustomDateAnalysis && 
					(customAnalysisType === "date" || customAnalysisType === "time");
				
				// Skip combineTab cards that don't meet criteria
				if (!isValidCombineTab) {
					continue;
				}
			}
		}

		const itemIds = selectedItemsByCard[cardId] || [];
		const groups = groupsByCard[cardId] || [];
		
		// Skip card only if it has neither items nor groups
		if (!itemIds.length && (!groups || groups.length === 0)) {
			continue;
		}

			// Process individual items
		for (const itemId of itemIds) {
			// Skip if this is the base card comparing to itself with the same item
			// This prevents comparing the base series to itself (which would always be 100% match)
			if (cardId === baseCardType) {
				const baseItemIds = selectedItemsByCard[baseCardType] || [];
				if (baseItemIds.includes(itemId)) {
					continue;
				}
			}
			
			const subset = filterPatientsByCardItemHospital(
				filteredAllPatients, // Use filtered patients (baseCardType filters excluded from cardFilter)
				cardId,
				itemId,
				dynamicCards,
				filterListData,
				ninetyDaysData // Pass ninetyDaysData for 90 days card filtering
			);

			if (!subset.length) {
				continue;
			}

			// Use filter with census data for accurate monthly calculations
			const filterWithCensus = {
				...filter,
				censusAverage: dialogFilters?.censusAverage,
				censusByPeriod: dialogFilters?.censusByPeriod || [],
			};
			let series = await buildSeriesForGroupType(
				subset,
				filterWithCensus,
				groupByType,
				pageType,
				transferType
			);
			if (!series.length) {
				continue;
			}

			// CRITICAL: Filter candidate series by isolated date ranges if provided
			// This ensures analysis only considers data from the selected date ranges
			// Both base and candidate series must be filtered by the same ranges for accurate comparison
			if (isolatedDateRanges.length > 0) {
				series = filterSeriesByIsolatedRanges(series, isolatedDateRanges, currentFilterType);
				if (!series.length) {
					// If filtering removes all points, skip this candidate
					continue;
				}
			}

			const candidateSlope = computeSlope(series);

			// Slope-based similarity
			let slopeSimilarity;
			const eps = 1e-6;
			if (Math.abs(baseSlope) < eps) {
				// Reference is essentially flat: compare absolute slopes
				const diffPercent = (Math.abs(candidateSlope) / (eps * 10)) * 100;
				const clampedDiff = Math.min(100, diffPercent);
				slopeSimilarity = Math.max(0, 100 - clampedDiff);
			} else {
				const diffPercent =
					(Math.abs(candidateSlope - baseSlope) / Math.abs(baseSlope)) * 100;
				const clampedDiff = Math.min(100, diffPercent);
				slopeSimilarity = Math.max(0, 100 - clampedDiff);
			}

			// Point-by-point similarity
			const pointSimilarity = computePointByPointSimilarity(baseSeries, series);

			// Calculate match percentage based on matching mode
			const matchingMode = TREND_MATCHING_MODE;
			let matchPercentage = 0;
			
			if (matchingMode === "trendOnly") {
				// Match only trend shape (ignore actual values)
				matchPercentage = slopeSimilarity;
			} else if (matchingMode === "valuesOnly") {
				// Match only point values (ignore trend direction)
				matchPercentage = pointSimilarity;
			} else if (matchingMode === "percentageBased") {
				// Match based on percentage distribution
				matchPercentage = computePercentageBasedSimilarity(baseSeries, series);
			} else {
				// Weighted combination (default)
				// Use TREND_WEIGHT constant for slope, rest for point values
				const trendWeight = TREND_WEIGHT / 100;
				const valuesWeight = 1 - trendWeight;
				matchPercentage = (slopeSimilarity * trendWeight) + (pointSimilarity * valuesWeight);
			}
			
			// Calculate totalCount as the sum of all values in the series (not patient count)
			// This represents the actual total count of events/data points
			const totalCount = series.reduce((sum, point) => sum + Math.abs(point.total || 0), 0);
			
			const matchResult = {
				cardId,
				cardLabel: getCardLabel(cardId),
				itemId,
				itemLabel: getItemLabel(cardId, itemId),
				matchPercentage,
				slope: candidateSlope,
				totalCount: totalCount,
				series: series, // Store series data for mini graph
				dashboardId: "CURRENT",
				dashboardLabel: getDashboardLabel(pageType),
			};
			matchesByCard.push(matchResult);
		}

		// Handle user-defined groups for this card (virtual targets)
		// Note: groups variable is already defined above for the skip check
		for (const group of groups) {
			const itemIdsForGroup = Array.isArray(group.itemIds)
				? group.itemIds
				: [];
			if (itemIdsForGroup.length < 2) continue;

			let groupPatients = [];
			itemIdsForGroup.forEach((itemId) => {
				const subset = filterPatientsByCardItemHospital(
					filteredAllPatients, // Use filtered patients (respects filter mode)
					cardId,
					itemId,
					dynamicCards,
					filterListData,
					ninetyDaysData // Pass ninetyDaysData for 90 days card filtering
				);
				if (subset && subset.length) {
					groupPatients = groupPatients.concat(subset);
				}
			});
			groupPatients = _.uniqBy(groupPatients, (p) =>
				String(p._id || p.id || "")
			);
			if (!groupPatients.length) continue;

			// Use filter with census data for accurate monthly calculations
			const filterWithCensus = {
				...filter,
				censusAverage: dialogFilters?.censusAverage,
				censusByPeriod: dialogFilters?.censusByPeriod || [],
			};
			let series = await buildSeriesForGroupType(
				groupPatients,
				filterWithCensus,
				groupByType,
				pageType,
				transferType
			);
			if (!series.length) continue;

			// CRITICAL: Filter group series by isolated date ranges if provided
			// This ensures analysis only considers data from the selected date ranges
			// Both base and group series must be filtered by the same ranges for accurate comparison
			if (isolatedDateRanges.length > 0) {
				series = filterSeriesByIsolatedRanges(series, isolatedDateRanges, currentFilterType);
				if (!series.length) {
					// If filtering removes all points, skip this group
					continue;
				}
			}

			const candidateSlope = computeSlope(series);

			// Slope-based similarity
			let slopeSimilarity;
			const eps = 1e-6;
			if (Math.abs(baseSlope) < eps) {
				const diffPercent = (Math.abs(candidateSlope) / (eps * 10)) * 100;
				const clampedDiff = Math.min(100, diffPercent);
				slopeSimilarity = Math.max(0, 100 - clampedDiff);
			} else {
				const diffPercent =
					(Math.abs(candidateSlope - baseSlope) / Math.abs(baseSlope)) * 100;
				const clampedDiff = Math.min(100, diffPercent);
				slopeSimilarity = Math.max(0, 100 - clampedDiff);
			}

			// Point-by-point similarity
			const pointSimilarity = computePointByPointSimilarity(baseSeries, series);

			// Calculate match percentage based on matching mode
			const matchingMode = TREND_MATCHING_MODE;
			let matchPercentage = 0;
			
			if (matchingMode === "trendOnly") {
				// Match only trend shape (ignore actual values)
				matchPercentage = slopeSimilarity;
			} else if (matchingMode === "valuesOnly") {
				// Match only point values (ignore trend direction)
				matchPercentage = pointSimilarity;
			} else if (matchingMode === "percentageBased") {
				// Match based on percentage distribution
				matchPercentage = computePercentageBasedSimilarity(baseSeries, series);
			} else {
				// Weighted combination (default)
				// Use TREND_WEIGHT constant for slope, rest for point values
				const trendWeight = TREND_WEIGHT / 100;
				const valuesWeight = 1 - trendWeight;
				matchPercentage = (slopeSimilarity * trendWeight) + (pointSimilarity * valuesWeight);
			}

			matchesByCard.push({
				cardId,
				cardLabel: getCardLabel(cardId),
				itemId: group.id,
				itemLabel: group.name,
				matchPercentage,
				slope: candidateSlope,
				isGroup: true,
				totalCount: groupPatients.length,
				series: series, // Store series data for mini graph
				dashboardId: "CURRENT",
				dashboardLabel: getDashboardLabel(pageType),
			});
		}
	}

	// Count matches above tolerance: matchPercentage is 100 - diffPercent,
	// so we treat "within similarityTolerance%" as a match.
	const threshold = 100 - similarityTolerance;
	let mergedMatches = [...matchesByCard];
	if (crossDashboardSelections.length > 0) {
		for (const selection of crossDashboardSelections) {
			if (!selection?.pageType || !Array.isArray(selection?.selectedCards) || selection.selectedCards.length === 0) {
				continue;
			}

			const compareResult = await analyzeTrendsLocally({
				pageType: selection.pageType,
				dialogFilters: selection.dialogFilters || {},
				config: {
					selectedCards: selection.selectedCards || [],
					selectedItemsByCard: selection.selectedItemsByCard || {},
					selectedGroupsByCard: selection.selectedGroupsByCard || {},
					groupsByCard: selection.groupsByCard || {},
					filterMode: "removeAll",
					selectedFilterCards: [],
					similarityTolerance,
					baseSeriesOverride: baseSeries,
					baseSlopeOverride: baseSlope,
					...(isolatedDateRanges.length > 0 && {
						isolatedDateRanges,
						currentFilterType,
					}),
				},
				baseCardType: null,
				patientsOverride: selection.patientsOverride || [],
				groupByType,
			});

			if (compareResult?.matchesByCard?.length > 0) {
				const dashboardLabel = selection.dashboardLabel || selection.dashboardId || "Compare";
				const dashboardId = selection.dashboardId || "COMPARE";
				const prefixed = compareResult.matchesByCard.map((entry) => ({
					...entry,
					cardId: `compare__${dashboardId}__${entry.cardId}`,
					// Keep the original cardLabel for the UI to use with the dashboard metadata
					// But also keep the fallback combined label for backward compatibility if needed
					cardLabel: entry.cardLabel,
					dashboardId,
					dashboardLabel,
				}));
				mergedMatches = mergedMatches.concat(prefixed);
			}
		}
	}

	const totalMatches = mergedMatches.filter(
		(m) => m.matchPercentage >= threshold
	).length;

	// Sort by highest match percentage
	const sortedMatches = _.orderBy(
		mergedMatches,
		["matchPercentage"],
		["desc"]
	);

	return {
		totalMatches,
		matchesByCard: sortedMatches,
	};
};


